import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Search, MapPin, Briefcase, GraduationCap, TrendingUp, Sparkles, 
  CheckCircle, ArrowRight, Lock, Compass, HelpCircle, Send, 
  Copy, ExternalLink, Globe, Phone, Map, User, Award, FileText, 
  ChevronRight, ChevronDown, Star, Check, Info, ShieldCheck, Mail, Bookmark, Sparkle,
  RefreshCw, AlertCircle, ThumbsUp, ThumbsDown, Heart, Bug, Lightbulb, ShieldAlert, Link as LinkIcon, Users, Building2, Target
} from "lucide-react";
import BizNearYouLogo from "./BizNearYouLogo";
import HomePageRebuild from "./HomePageRebuild";
import { ThemeSelector, ThemePreference } from "./ThemeSelector";
import { LanguageSelector } from "./LanguageSelector";
import { seoConfig } from "../utils/seoConfig";
import { SeoBreadcrumbs } from "./seo/SeoBreadcrumbs";
import { 
  CONTACT_TYPE_CONFIG, 
  RELATIONSHIP_OPTIONS, 
  validateContactSubmission, 
  ContactSubmissionType, 
  RemovalRelationship 
} from "../utils/contactValidation";
import { PricingComparisonTable } from "./PricingComparisonTable";
import { fetchJsonWithRetry } from "../lib/fetchWithRetry";
import { AI_FEATURES_ENABLED } from "../config/featureFlags";
import { getCategorySlugFromText } from "../utils/jobCategoryRegistry";
import { resolveLocationSlug } from "../utils/locationRoutes";
import { resolveCanonicalSearchIntent } from "../utils/canonicalIntentResolver";

// Types
export interface PublicPageProps {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentPath: string;
  currentUser?: any;
  settingsAppTheme?: string;
  onThemeChange?: (theme: ThemePreference) => void;
}

// 0. Custom SEO Hook
export function useSeoMetadata({
  title,
  description,
  canonicalUrl,
  schemaJson,
  robots = "index, follow"
}: {
  title: string;
  description: string;
  canonicalUrl: string;
  schemaJson?: object;
  robots?: string;
}) {
  useEffect(() => {
    // 1. Update Title
    document.title = title;

    // 2. Update Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", description);

    // 3. Update Canonical Link
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute("href", canonicalUrl);

    // Helper to upsert meta tags
    const upsertMeta = (name: string, content: string, isProperty = false) => {
      const attrName = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attrName}="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attrName, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    // Update Open Graph Tags
    upsertMeta("og:title", title, true);
    upsertMeta("og:description", description, true);
    upsertMeta("og:url", canonicalUrl, true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:image", "https://getbiznearyou.com/favicon-512.png", true);

    // Update Twitter Cards
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", title);
    upsertMeta("twitter:description", description);
    upsertMeta("twitter:image", "https://getbiznearyou.com/favicon-512.png");

    // Update Robots
    upsertMeta("robots", robots);

    // 4. Update Schema JSON-LD (Filter out BreadcrumbList to prevent duplicate BreadcrumbList schemas)
    let scriptSchema = document.getElementById("seo-schema-jsonld");
    let cleanedSchema = schemaJson ? JSON.parse(JSON.stringify(schemaJson)) : undefined;
    if (cleanedSchema) {
      if (cleanedSchema["@type"] === "BreadcrumbList") {
        cleanedSchema = undefined;
      } else if (Array.isArray(cleanedSchema["@graph"])) {
        cleanedSchema["@graph"] = cleanedSchema["@graph"].filter((item: any) => item && item["@type"] !== "BreadcrumbList");
        if (cleanedSchema["@graph"].length === 0) {
          cleanedSchema = undefined;
        }
      }
    }

    if (cleanedSchema) {
      if (!scriptSchema) {
        scriptSchema = document.createElement("script");
        scriptSchema.setAttribute("id", "seo-schema-jsonld");
        scriptSchema.setAttribute("type", "application/ld+json");
        document.head.appendChild(scriptSchema);
      }
      scriptSchema.textContent = JSON.stringify(cleanedSchema);
    } else if (scriptSchema) {
      scriptSchema.remove();
    }
  }, [title, description, canonicalUrl, schemaJson, robots]);
}

// 1. Shared Public Header
export function PublicHeader({ onNavigate, onLogin, currentPath, currentUser, settingsAppTheme = "auto", onThemeChange }: PublicPageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExploreOpen, setIsExploreOpen] = useState(false);
  const [activeMobileSection, setActiveMobileSection] = useState<string | null>("browse");

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    setIsExploreOpen(false);
    setIsOpen(false);
    onNavigate(path);
  };

  const navItems = [
    { name: "Find Employers", path: "/find-jobs" },
    { name: "Direct Outreach", path: "/hidden-jobs" },
    { name: "Local Internships", path: "/local-internships" },
    { name: "How It Works", path: "/how-it-works" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 px-4 sm:px-6 py-3.5 transition-colors">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <a 
          href="/" 
          onClick={(e) => handleLinkClick(e, "/")} 
          className="flex items-center gap-2 cursor-pointer"
        >
          <BizNearYouLogo textColor="text-slate-900 dark:text-white" className="scale-105" isDark={false} />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1.5">
          {navItems.slice(0, 3).map((item) => {
            const isActive = currentPath === item.path;
            return (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => handleLinkClick(e, item.path)}
                className={`h-9 px-3.5 inline-flex items-center justify-center rounded-lg text-sm font-medium transition duration-150 cursor-pointer ${
                  isActive 
                    ? "bg-slate-100 dark:bg-slate-800 text-blue-600 dark:text-blue-400 font-semibold" 
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                {item.name}
              </a>
            );
          })}

          <a
            href="/how-it-works"
            onClick={(e) => handleLinkClick(e, "/how-it-works")}
            className={`h-9 px-3.5 inline-flex items-center justify-center rounded-lg text-sm font-medium transition duration-150 cursor-pointer ${
              currentPath === "/how-it-works" 
                ? "bg-slate-100 dark:bg-slate-800 text-blue-600 dark:text-blue-400 font-semibold" 
                : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            How It Works
          </a>
          <a
            href="/docs"
            onClick={(e) => handleLinkClick(e, "/docs")}
            className={`h-9 px-3.5 inline-flex items-center justify-center rounded-lg text-sm font-medium transition duration-150 cursor-pointer ${
              currentPath === "/docs" 
                ? "bg-slate-100 dark:bg-slate-800 text-blue-600 dark:text-blue-400 font-semibold" 
                : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            Docs & FAQ
          </a>

          {/* Explore Mega Menu Trigger (Combines Explore & Solutions) */}
          <div 
            className="relative"
            onMouseEnter={() => setIsExploreOpen(true)}
            onMouseLeave={() => setIsExploreOpen(false)}
          >
            <button
              onClick={() => setIsExploreOpen(!isExploreOpen)}
              className={`h-9 px-3.5 inline-flex items-center justify-center rounded-lg text-sm font-medium transition duration-150 cursor-pointer gap-1.5 ${
                isExploreOpen || currentPath === "/explore" || currentPath.startsWith("/for-") || currentPath.startsWith("/locations") || currentPath.startsWith("/jobs/") || currentPath.startsWith("/industries") || currentPath.startsWith("/guides") || currentPath.startsWith("/features")
                  ? "bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 font-semibold" 
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
              }`}
              aria-expanded={isExploreOpen}
            >
              <span>Explore</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExploreOpen ? "rotate-180 text-blue-600 dark:text-blue-400" : "text-slate-400"}`} />
            </button>

            {/* Combined Mega Menu Dropdown */}
            <AnimatePresence>
              {isExploreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full pt-2 w-[860px] z-50"
                >
                  <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 grid grid-cols-4 gap-5 text-left ring-1 ring-slate-900/5 dark:ring-slate-100/5">
                    {/* Col 1: Find Opportunities */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-blue-600 dark:text-blue-400 border-b border-slate-100 dark:border-slate-800 pb-2">
                        <Compass className="w-3.5 h-3.5" />
                        Find Opportunities
                      </div>
                      <div className="flex flex-col gap-0.5 text-xs sm:text-sm">
                        <a href="/businesses-hiring-near-me" onClick={(e) => handleLinkClick(e, "/businesses-hiring-near-me")} className="hover:text-blue-600 dark:hover:text-blue-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Businesses Hiring Near Me
                        </a>
                        <a href="/hidden-jobs" onClick={(e) => handleLinkClick(e, "/hidden-jobs")} className="hover:text-blue-600 dark:hover:text-blue-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Hidden Jobs CRM
                        </a>
                        <a href="/local-internships" onClick={(e) => handleLinkClick(e, "/local-internships")} className="hover:text-blue-600 dark:hover:text-blue-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Local Internships
                        </a>
                        <a href="/first-jobs" onClick={(e) => handleLinkClick(e, "/first-jobs")} className="hover:text-blue-600 dark:hover:text-blue-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          First Jobs & Student Gigs
                        </a>
                        <a href="/find-jobs" onClick={(e) => handleLinkClick(e, "/find-jobs")} className="hover:text-blue-600 dark:hover:text-blue-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Local Job-Search Tools
                        </a>
                      </div>
                    </div>

                    {/* Col 2: Solutions by Role */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-slate-100 dark:border-slate-800 pb-2">
                        <Users className="w-3.5 h-3.5" />
                        Solutions by Role
                      </div>
                      <div className="flex flex-col gap-0.5 text-xs sm:text-sm">
                        <a href="/for" onClick={(e) => handleLinkClick(e, "/for")} className="text-indigo-700 dark:text-indigo-300 bg-indigo-50/70 dark:bg-indigo-950/60 hover:bg-indigo-100/70 dark:hover:bg-indigo-900/60 p-1.5 rounded-lg transition font-bold flex items-center justify-between mb-1">
                          Audience Directory Hub <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                        <a href="/for-job-seekers" onClick={(e) => handleLinkClick(e, "/for-job-seekers")} className="hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Job Seekers
                        </a>
                        <a href="/for-recruiters" onClick={(e) => handleLinkClick(e, "/for-recruiters")} className="hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Recruiters & Headhunters
                        </a>
                        <a href="/for-staffing-agencies" onClick={(e) => handleLinkClick(e, "/for-staffing-agencies")} className="hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Staffing Agencies
                        </a>
                        <a href="/for-career-coaches" onClick={(e) => handleLinkClick(e, "/for-career-coaches")} className="hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Career Coaches & Advisors
                        </a>
                        <a href="/for-workforce-organizations" onClick={(e) => handleLinkClick(e, "/for-workforce-organizations")} className="hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Workforce Organizations
                        </a>
                        <a href="/for-college-career-centers" onClick={(e) => handleLinkClick(e, "/for-college-career-centers")} className="hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          College Career Centers
                        </a>
                        <a href="/for-sales-teams" onClick={(e) => handleLinkClick(e, "/for-sales-teams")} className="hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Sales Teams & BizDev
                        </a>
                        <a href="/for-agencies" onClick={(e) => handleLinkClick(e, "/for-agencies")} className="hover:text-indigo-600 dark:hover:text-indigo-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Agencies & Freelancers
                        </a>
                      </div>
                    </div>

                    {/* Col 3: Browse Directories */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 border-b border-slate-100 dark:border-slate-800 pb-2">
                        <Globe className="w-3.5 h-3.5" />
                        Browse
                      </div>
                      <div className="flex flex-col gap-0.5 text-xs sm:text-sm">
                        <a href="/explore" onClick={(e) => handleLinkClick(e, "/explore")} className="text-emerald-700 dark:text-emerald-300 bg-emerald-50/70 dark:bg-emerald-950/60 hover:bg-emerald-100/70 dark:hover:bg-emerald-900/60 p-1.5 rounded-lg transition font-bold flex items-center justify-between">
                          Explore Central Hub <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                        <a href="/locations" onClick={(e) => handleLinkClick(e, "/locations")} className="hover:text-emerald-600 dark:hover:text-emerald-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Locations Directory
                        </a>
                        <a href="/jobs/locations" onClick={(e) => handleLinkClick(e, "/jobs/locations")} className="hover:text-emerald-600 dark:hover:text-emerald-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Jobs by Location
                        </a>
                        <a href="/businesses-hiring/locations" onClick={(e) => handleLinkClick(e, "/businesses-hiring/locations")} className="hover:text-emerald-600 dark:hover:text-emerald-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Hiring Signals by Location
                        </a>
                        <a href="/internships/locations" onClick={(e) => handleLinkClick(e, "/internships/locations")} className="hover:text-emerald-600 dark:hover:text-emerald-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Internships by Location
                        </a>
                        <a href="/industries" onClick={(e) => handleLinkClick(e, "/industries")} className="hover:text-emerald-600 dark:hover:text-emerald-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Industry Directories
                        </a>
                        <a href="/guides" onClick={(e) => handleLinkClick(e, "/guides")} className="hover:text-emerald-600 dark:hover:text-emerald-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Career Guides
                        </a>
                      </div>
                    </div>

                    {/* Col 4: Learn & Methodology */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-purple-600 dark:text-purple-400 border-b border-slate-100 dark:border-slate-800 pb-2">
                        <Lightbulb className="w-3.5 h-3.5" />
                        Learn & Methodology
                      </div>
                      <div className="flex flex-col gap-0.5 text-xs sm:text-sm">
                        <a href="/how-it-works" onClick={(e) => handleLinkClick(e, "/how-it-works")} className="hover:text-purple-600 dark:hover:text-purple-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          How It Works
                        </a>
                        <a href="/features" onClick={(e) => handleLinkClick(e, "/features")} className="hover:text-purple-600 dark:hover:text-purple-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Platform Features
                        </a>
                        <a href="/methodology/hiring-signals" onClick={(e) => handleLinkClick(e, "/methodology/hiring-signals")} className="hover:text-purple-600 dark:hover:text-purple-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Hiring Signals
                        </a>
                        <a href="/methodology/opportunity-score" onClick={(e) => handleLinkClick(e, "/methodology/opportunity-score")} className="hover:text-purple-600 dark:hover:text-purple-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Opportunity Score
                        </a>
                        <a href="/methodology/data-sources" onClick={(e) => handleLinkClick(e, "/methodology/data-sources")} className="hover:text-purple-600 dark:hover:text-purple-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Data Sources
                        </a>
                        <a href="/docs" onClick={(e) => handleLinkClick(e, "/docs")} className="hover:text-purple-600 dark:hover:text-purple-400 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 p-1.5 rounded-lg transition font-medium">
                          Documentation & FAQ
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        {/* Action Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          <ThemeSelector 
            currentTheme={settingsAppTheme} 
            onThemeChange={onThemeChange || (() => {})} 
            variant="dropdown" 
            idPrefix="pub-head-desktop" 
          />
          <LanguageSelector
            variant="dropdown"
            idPrefix="pub-head-desktop-lang"
          />
          {currentUser ? (
            <a
              href="/dashboard"
              onClick={(e) => handleLinkClick(e, "/dashboard")}
              className="h-9 px-4 inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-lg shadow-sm hover:shadow transition cursor-pointer gap-1.5"
            >
              Back to Dashboard <ArrowRight className="w-4 h-4" />
            </a>
          ) : (
            <>
              <button
                onClick={() => onLogin(false)}
                className="h-9 px-4 inline-flex items-center justify-center text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-950 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition cursor-pointer"
              >
                Log In
              </button>
              <button
                onClick={() => onLogin(true)}
                className="h-9 px-4 inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-lg shadow-sm hover:shadow transition cursor-pointer gap-1.5"
              >
                Get Started Free <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <div className="lg:hidden flex items-center gap-2">
          {currentUser ? (
            <a
              href="/dashboard"
              onClick={(e) => handleLinkClick(e, "/dashboard")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs px-3 py-2 rounded shadow-sm transition cursor-pointer"
            >
              Dashboard
            </a>
          ) : (
            <button
              onClick={() => onLogin(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs px-3 py-2 rounded shadow-sm transition cursor-pointer"
            >
              Sign Up
            </button>
          )}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
            aria-label="Toggle navigation menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-slate-100 dark:border-slate-800 mt-3 pt-3 pb-4 space-y-3"
          >
            {/* Theme & Language Selectors for Mobile */}
            <div className="px-1 pt-1 pb-2 border-b border-slate-100 dark:border-slate-800 space-y-3">
              <div>
                <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 px-1">
                  Theme Preference
                </div>
                <ThemeSelector 
                  currentTheme={settingsAppTheme} 
                  onThemeChange={onThemeChange || (() => {})} 
                  variant="pills" 
                  idPrefix="pub-head-mobile-drawer" 
                />
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 px-1">
                  Language Preference
                </div>
                <LanguageSelector 
                  variant="pills" 
                  idPrefix="pub-head-mobile-drawer-lang" 
                />
              </div>
            </div>

            {/* Core Links */}
            <div className="space-y-1">
              <a href="/explore" onClick={(e) => handleLinkClick(e, "/explore")} className="block px-3 py-2 rounded-lg text-sm font-black bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 flex items-center justify-between">
                <span>Explore Central Hub</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Accordion 1: Find Opportunities */}
            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
              <button
                onClick={() => setActiveMobileSection(activeMobileSection === "opportunities" ? null : "opportunities")}
                className="w-full text-left px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/80 flex items-center justify-between"
              >
                <span>Find Opportunities</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${activeMobileSection === "opportunities" ? "rotate-180 text-blue-600 dark:text-blue-400" : "text-slate-400"}`} />
              </button>
              {activeMobileSection === "opportunities" && (
                <div className="p-2 space-y-1 bg-white dark:bg-slate-900 text-sm">
                  <a href="/businesses-hiring-near-me" onClick={(e) => handleLinkClick(e, "/businesses-hiring-near-me")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Businesses Hiring Near Me</a>
                  <a href="/hidden-jobs" onClick={(e) => handleLinkClick(e, "/hidden-jobs")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Hidden Jobs CRM</a>
                  <a href="/local-internships" onClick={(e) => handleLinkClick(e, "/local-internships")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Local Internships</a>
                  <a href="/first-jobs" onClick={(e) => handleLinkClick(e, "/first-jobs")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">First Jobs & Student Gigs</a>
                  <a href="/find-jobs" onClick={(e) => handleLinkClick(e, "/find-jobs")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Local Job-Search Tools</a>
                </div>
              )}
            </div>

            {/* Accordion 2: Solutions by Role */}
            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
              <button
                onClick={() => setActiveMobileSection(activeMobileSection === "solutions" ? null : "solutions")}
                className="w-full text-left px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/80 flex items-center justify-between"
              >
                <span>Solutions by Role</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${activeMobileSection === "solutions" ? "rotate-180 text-indigo-600 dark:text-indigo-400" : "text-slate-400"}`} />
              </button>
              {activeMobileSection === "solutions" && (
                <div className="p-2 space-y-1 bg-white dark:bg-slate-900 text-sm">
                  <a href="/for-job-seekers" onClick={(e) => handleLinkClick(e, "/for-job-seekers")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Job Seekers</a>
                  <a href="/for-recruiters" onClick={(e) => handleLinkClick(e, "/for-recruiters")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Recruiters</a>
                  <a href="/for-staffing-agencies" onClick={(e) => handleLinkClick(e, "/for-staffing-agencies")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Staffing Agencies</a>
                  <a href="/for-career-coaches" onClick={(e) => handleLinkClick(e, "/for-career-coaches")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Career Coaches</a>
                  <a href="/for-workforce-organizations" onClick={(e) => handleLinkClick(e, "/for-workforce-organizations")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Workforce Orgs</a>
                  <a href="/for-college-career-centers" onClick={(e) => handleLinkClick(e, "/for-college-career-centers")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">College Career Centers</a>
                  <a href="/for-sales-teams" onClick={(e) => handleLinkClick(e, "/for-sales-teams")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Sales Teams & BizDev</a>
                  <a href="/for-agencies" onClick={(e) => handleLinkClick(e, "/for-agencies")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Agencies & Freelancers</a>
                </div>
              )}
            </div>

            {/* Accordion 2: Browse Directories */}
            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
              <button
                onClick={() => setActiveMobileSection(activeMobileSection === "browse" ? null : "browse")}
                className="w-full text-left px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/80 flex items-center justify-between"
              >
                <span>Browse Directories</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${activeMobileSection === "browse" ? "rotate-180 text-blue-600 dark:text-blue-400" : "text-slate-400"}`} />
              </button>
              {activeMobileSection === "browse" && (
                <div className="p-2 space-y-1 bg-white dark:bg-slate-900 text-sm">
                  <a href="/locations" onClick={(e) => handleLinkClick(e, "/locations")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Locations Directory</a>
                  <a href="/jobs/locations" onClick={(e) => handleLinkClick(e, "/jobs/locations")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Jobs by Location</a>
                  <a href="/businesses-hiring/locations" onClick={(e) => handleLinkClick(e, "/businesses-hiring/locations")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Hiring Signals by Location</a>
                  <a href="/internships/locations" onClick={(e) => handleLinkClick(e, "/internships/locations")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Internships by Location</a>
                  <a href="/industries" onClick={(e) => handleLinkClick(e, "/industries")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Industry Directories</a>
                  <a href="/guides" onClick={(e) => handleLinkClick(e, "/guides")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Career Guides</a>
                </div>
              )}
            </div>

            {/* Accordion 3: Learn & Methodology */}
            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
              <button
                onClick={() => setActiveMobileSection(activeMobileSection === "learn" ? null : "learn")}
                className="w-full text-left px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/80 flex items-center justify-between"
              >
                <span>Learn & Methodology</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${activeMobileSection === "learn" ? "rotate-180 text-blue-600 dark:text-blue-400" : "text-slate-400"}`} />
              </button>
              {activeMobileSection === "learn" && (
                <div className="p-2 space-y-1 bg-white dark:bg-slate-900 text-sm">
                  <a href="/how-it-works" onClick={(e) => handleLinkClick(e, "/how-it-works")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">How It Works</a>
                  <a href="/features" onClick={(e) => handleLinkClick(e, "/features")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Platform Features</a>
                  <a href="/methodology/hiring-signals" onClick={(e) => handleLinkClick(e, "/methodology/hiring-signals")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Hiring Signals Methodology</a>
                  <a href="/methodology/opportunity-score" onClick={(e) => handleLinkClick(e, "/methodology/opportunity-score")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Opportunity Score</a>
                  <a href="/methodology/data-sources" onClick={(e) => handleLinkClick(e, "/methodology/data-sources")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Data Sources</a>
                  <a href="/docs" onClick={(e) => handleLinkClick(e, "/docs")} className="block p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">Documentation & FAQ</a>
                </div>
              )}
            </div>

            <div className="pt-3 px-2 flex flex-col gap-2">
              {currentUser ? (
                <a
                  href="/dashboard"
                  onClick={(e) => handleLinkClick(e, "/dashboard")}
                  className="w-full text-center py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 cursor-pointer"
                >
                  Back to Dashboard
                </a>
              ) : (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onLogin(false);
                  }}
                  className="w-full py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                >
                  Log In
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// 2. Shared Public Footer
export function PublicFooter({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  const currentYear = new Date().getFullYear();

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <footer className="bg-slate-900 dark:bg-slate-950 text-slate-300 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16 grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-10">
        {/* Brand */}
        <div className="md:col-span-1 space-y-4">
          <BizNearYouLogo textColor="text-white" className="scale-100" isDark={true} />
          <p className="text-sm text-slate-400 leading-relaxed">
            BizNearYou is a modern research tool and outreach CRM that helps people discover, prioritize, organize, and contact local businesses worth reaching out to for jobs and internships.
          </p>
        </div>

        {/* Group 1: Explore */}
        <div>
          <h4 className="text-white font-bold text-xs tracking-wider uppercase mb-4">Explore</h4>
          <ul className="space-y-2.5 text-sm text-slate-400 text-left">
            <li>
              <a href="/explore" onClick={(e) => handleLinkClick(e, "/explore")} className="hover:text-white transition cursor-pointer font-semibold text-blue-400">
                Explore All Resources
              </a>
            </li>
            <li>
              <a href="/locations" onClick={(e) => handleLinkClick(e, "/locations")} className="hover:text-white transition cursor-pointer">
                Locations Directory
              </a>
            </li>
            <li>
              <a href="/guides" onClick={(e) => handleLinkClick(e, "/guides")} className="hover:text-white transition cursor-pointer">
                Career Guides
              </a>
            </li>
            <li>
              <a href="/industries" onClick={(e) => handleLinkClick(e, "/industries")} className="hover:text-white transition cursor-pointer">
                Industries Directory
              </a>
            </li>
            <li>
              <a href="/features" onClick={(e) => handleLinkClick(e, "/features")} className="hover:text-white transition cursor-pointer">
                Platform Features
              </a>
            </li>
            <li>
              <a href="/compare" onClick={(e) => handleLinkClick(e, "/compare")} className="hover:text-white transition cursor-pointer">
                Methodology Comparisons
              </a>
            </li>
          </ul>
        </div>

        {/* Group 2: Find Opportunities */}
        <div>
          <h4 className="text-white font-bold text-xs tracking-wider uppercase mb-4">Find Opportunities</h4>
          <ul className="space-y-2.5 text-sm text-slate-400 text-left">
            <li>
              <a href="/businesses-hiring-near-me" onClick={(e) => handleLinkClick(e, "/businesses-hiring-near-me")} className="hover:text-white transition cursor-pointer">
                Businesses Hiring Near Me
              </a>
            </li>
            <li>
              <a href="/hidden-jobs" onClick={(e) => handleLinkClick(e, "/hidden-jobs")} className="hover:text-white transition cursor-pointer">
                Hidden Jobs CRM
              </a>
            </li>
            <li>
              <a href="/local-internships" onClick={(e) => handleLinkClick(e, "/local-internships")} className="hover:text-white transition cursor-pointer">
                Local Internships
              </a>
            </li>
            <li>
              <a href="/first-jobs" onClick={(e) => handleLinkClick(e, "/first-jobs")} className="hover:text-white transition cursor-pointer">
                First Jobs & Student Gigs
              </a>
            </li>
            <li>
              <a href="/find-jobs" onClick={(e) => handleLinkClick(e, "/find-jobs")} className="hover:text-white transition cursor-pointer">
                Local Job-Search Tools
              </a>
            </li>
          </ul>
        </div>

        {/* Group 3: Learn */}
        <div>
          <h4 className="text-white font-bold text-xs tracking-wider uppercase mb-4">Learn</h4>
          <ul className="space-y-2.5 text-sm text-slate-400 text-left">
            <li>
              <a href="/how-it-works" onClick={(e) => handleLinkClick(e, "/how-it-works")} className="hover:text-white transition cursor-pointer">
                How It Works
              </a>
            </li>
            <li>
              <a href="/methodology/hiring-signals" onClick={(e) => handleLinkClick(e, "/methodology/hiring-signals")} className="hover:text-white transition cursor-pointer">
                Hiring Signals Methodology
              </a>
            </li>
            <li>
              <a href="/docs" onClick={(e) => handleLinkClick(e, "/docs")} className="hover:text-white transition cursor-pointer">
                Documentation & Guides
              </a>
            </li>
            <li>
              <a href="/docs?tab=faq" onClick={(e) => handleLinkClick(e, "/docs")} className="hover:text-white transition cursor-pointer">
                Frequently Asked Questions
              </a>
            </li>
          </ul>
        </div>

        {/* Group 4: Company */}
        <div>
          <h4 className="text-white font-bold text-xs tracking-wider uppercase mb-4">Company</h4>
          <ul className="space-y-2.5 text-sm text-slate-400 text-left">
            <li>
              <a href="/about" onClick={(e) => handleLinkClick(e, "/about")} className="hover:text-white transition cursor-pointer">
                About BizNearYou
              </a>
            </li>
            <li>
              <a href="/contact" onClick={(e) => handleLinkClick(e, "/contact")} className="hover:text-white transition cursor-pointer">
                Contact Support
              </a>
            </li>
            <li>
              <a href="/privacy" onClick={(e) => handleLinkClick(e, "/privacy")} className="hover:text-white transition cursor-pointer">
                Privacy Policy
              </a>
            </li>
            <li>
              <a href="/terms" onClick={(e) => handleLinkClick(e, "/terms")} className="hover:text-white transition cursor-pointer">
                Terms of Service
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-slate-950/80 dark:bg-slate-950 px-6 py-6 border-t border-slate-900/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <span>&copy; {currentYear} BizNearYou. All rights reserved. Made for proactive careers.</span>
          <div className="flex gap-4">
            <a href="/privacy" onClick={(e) => handleLinkClick(e, "/privacy")} className="hover:text-slate-400 transition cursor-pointer">Privacy Policy</a>
            <a href="/terms" onClick={(e) => handleLinkClick(e, "/terms")} className="hover:text-slate-400 transition cursor-pointer">Terms of Service</a>
            <a href="/docs" onClick={(e) => handleLinkClick(e, "/docs")} className="hover:text-slate-400 transition cursor-pointer">Docs & FAQ</a>
            <a href="/contact" onClick={(e) => handleLinkClick(e, "/contact")} className="hover:text-slate-400 transition cursor-pointer">Contact Support</a>
            <span className="hover:text-slate-400 transition text-slate-400 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
              All Systems Operational
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// 3. Homepage (/)
export function PublicHomepage({
  onNavigate,
  onLogin,
  onHomepageSearch
}: Omit<PublicPageProps, "currentPath"> & {
  onHomepageSearch?: (keyword: string, location: string) => void;
}) {
  useSeoMetadata({
    title: "Find Local Businesses That May Be Hiring Near You | BizNearYou",
    description: "Search nearby businesses, review recent hiring signals, and find employers you may want to contact directly—even when they have not posted a job on a major job board.",
    canonicalUrl: "https://getbiznearyou.com/",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "BizNearYou",
      "description": "Find local business opportunities, hiring signals, and unadvertised job openings before they hit major job boards.",
      "url": "https://getbiznearyou.com/"
    }
  });

  return (
    <HomePageRebuild
      onNavigate={onNavigate}
      onLogin={(isSignUp) => onLogin && onLogin(isSignUp)}
      onHomepageSearch={onHomepageSearch}
    />
  );
}

// 18. Hiring Signals Explained Page (/hiring-signals-explained)
export function HiringSignalsExplainedPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  useSeoMetadata({
    title: "Hiring Signals Explained: Modern Local Job Sourcing | BizNearYou",
    description: "Learn how we analyze public data—like review velocity, digital tool upgrades, and local demand—to detect active local business growth and unadvertised hiring needs.",
    canonicalUrl: "https://getbiznearyou.com/hiring-signals-explained",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Hiring Signals Explained: Modern Local Job Sourcing",
      "description": "An in-depth explanation of how public commercial activity signals can help job seekers identify hidden job opportunities before they are posted.",
      "url": "https://getbiznearyou.com/hiring-signals-explained"
    }
  });

  const signals = [
    {
      title: "Review Velocity & Customer Momentum",
      icon: <TrendingUp className="w-5 h-5 text-indigo-600" />,
      description: "When a neighborhood dental clinic, veterinary practice, or retail boutique experiences a sudden spike in public review frequency, it means high customer volume. More customers equals more operational load, which translates directly into a higher need for assistant staff, hosts, and administrative help.",
      indicator: "Green Flag: >5 new reviews in the past 30 days."
    },
    {
      title: "Digital Tool Upgrades",
      icon: <Sparkles className="w-5 h-5 text-emerald-600" />,
      description: "Physical businesses upgrading their tech stack (e.g. adding modern online booking forms, customer portals, or new CRM systems) indicates they are actively investing in their operations. This commercial growth is a strong leading indicator that they are ready to scale their human capital.",
      indicator: "Green Flag: Active booking integrations and modern responsive web layouts."
    },
    {
      title: "Commercial Footprint & Local Density",
      icon: <Map className="w-5 h-5 text-blue-600" />,
      description: "Businesses with multiple locations or newly registered regional check-ins show active business growth. When a business expands physically, managing the logistics requires boots on the ground. These are prime targets for proactive job seekers.",
      indicator: "Green Flag: Multiple registered regional locations with growing review scores."
    },
    {
      title: "Sustained Positive Sentiment",
      icon: <ThumbsUp className="w-5 h-5 text-amber-600" />,
      description: "Consistently high rating trends indicate healthy management and operational stability. A stable, well-reviewed business is much safer to join and has the commercial budget to support entry-level helper positions or internships.",
      indicator: "Green Flag: Stable rating of 4.3+ stars on maps."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      <div className="max-w-4xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "Hiring Signals Explained", path: "/hiring-signals-explained" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
            <Sparkle className="w-3.5 h-3.5 text-indigo-600" />
            <span>Core Methodology</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            How Hiring Signals Work
          </h1>
          <h2 className="text-lg text-slate-500 font-medium max-w-2xl leading-relaxed">
            We analyze billions of public data points to identify active local businesses that are busy, growing, and 5x more likely to have hidden hiring needs.
          </h2>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm space-y-4">
          <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
            <Info className="w-5 h-5 text-indigo-500" /> What is a "Hiring Signal"?
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            On BizNearYou, a "hiring signal" is not an active job post on a public board. Instead, it is an <strong>objective leading indicator of business activity</strong>. Traditional job boards only show you positions after they've been open for weeks and received hundreds of applications. By tracking activity signals, you find busy businesses <em>before</em> they post a job, allowing you to bypass the competition completely.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
          {signals.map((sig, idx) => (
            <div key={idx} className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm space-y-3 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-slate-50 rounded-lg">
                    {sig.icon}
                  </div>
                  <h3 className="font-bold text-slate-900 text-base">{sig.title}</h3>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">{sig.description}</p>
              </div>
              <div className="pt-3 border-t border-slate-50">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{sig.indicator}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8 text-center space-y-4 shadow-xs">
          <h3 className="font-bold text-base sm:text-lg">Put Sourcing Methodology Into Practice</h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 max-w-xl mx-auto leading-relaxed">
            Ready to find busy, local clinics, shops, and trade offices near you? Start searching by your city and find active growth signals in seconds.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <button
              onClick={() => onNavigate("/dashboard")}
              className="bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Go to CRM Dashboard
            </button>
            <button
              onClick={() => onNavigate("/")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Search Local Businesses
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 19. Standalone FAQ Page (/faq)
export function FaqPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  useSeoMetadata({
    title: "Frequently Asked Questions (FAQ) | BizNearYou",
    description: "Find complete and accurate answers to common questions about BizNearYou, local hiring signals, unadvertised job sourcing, and outreach etiquette.",
    canonicalUrl: "https://getbiznearyou.com/faq",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is BizNearYou?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "BizNearYou is an offline-first-inspired business research platform and private outreach CRM designed for starting professionals, high-school students, and proactive job seekers. It allows you to search for local businesses, analyze public-domain activity markers, and manage direct, professional outreach to access the hidden local job market."
          }
        },
        {
          "@type": "Question",
          "name": "How is BizNearYou different from Indeed and other job boards?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Indeed and traditional job boards only list active, advertised job openings which often attract hundreds of competing applicants. BizNearYou is not a job board; instead, it is a commercial research and outreach tracking system. We help you identify busy, growing local businesses that are 5x more likely to have upcoming or unadvertised helper needs, allowing you to establish direct contact before any official listing is posted."
          }
        },
        {
          "@type": "Question",
          "name": "What are Hiring Signals?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Hiring Signals on BizNearYou are objective, public indicators of strong local business activity and growth. Rather than a formal job vacancy, we analyze public-domain markers such as positive rating momentum on mapping directories, recent review volume spikes, active web-booking systems, or digital tech stack upgrades."
          }
        }
      ]
    }
  });

  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const faqs = [
    {
      id: "faq-what-is",
      q: "What is BizNearYou?",
      a: "BizNearYou is an offline-first-inspired business research platform and private outreach CRM designed for starting professionals, high-school students, and proactive job seekers. It allows you to search for local businesses, analyze public-domain activity markers, and manage direct, professional outreach to access hidden local job markets."
    },
    {
      id: "faq-difference",
      q: "How is BizNearYou different from Indeed and other job boards?",
      a: "Indeed and traditional job boards only list active, advertised job openings which often attract hundreds of competing applicants. BizNearYou is not a job board; instead, it is a commercial research and outreach tracking system. We help you identify busy, growing local businesses that are 5x more likely to have upcoming or unadvertised helper needs, allowing you to establish early direct contact before any official listing is posted."
    },
    {
      id: "faq-signals",
      q: "What are Hiring Signals?",
      a: "Hiring Signals on BizNearYou are objective, public indicators of strong local business activity and growth. Rather than a formal job vacancy, we analyze public-domain markers such as positive rating momentum on mapping directories, recent review volume spikes, active web-booking systems, or digital tech stack upgrades. A strong signal means a business is thriving and potentially under-staffed."
    },
    {
      id: "faq-signals-guarantee",
      q: "Does a Hiring Signal mean a business is definitely hiring?",
      a: "No. A Hiring Signal is a leading indicator of physical and commercial activity, not a guarantee of an active vacancy, job offer, or interview. It simply identifies healthy, thriving local businesses that are great candidates for proactive outreach. Users must always verify employment opportunities and active needs directly with each business."
    },
    {
      id: "faq-how-to-ask",
      q: "How do I ask a local business if they are hiring?",
      a: "We recommend a proactive, polite, and brief message. Introduce yourself as a local resident or student, explain what specific value you can bring (such as administrative, hosting, or customer support), and ask if they have any upcoming helper roles, part-time opportunities, or internships. Avoid reaching out during their busiest peak commercial hours."
    },
    {
      id: "faq-how-to-contact",
      q: "How do I contact a local business professionally?",
      a: "You can contact them via email, through their website contact form, or by visiting in person during off-peak hours (such as mid-afternoon for retail and hospitality, or mid-week for medical clinics). Always address the owner or manager by name if possible, be extremely polite to gatekeepers, attach a simple and clean resume, and follow up respectfully after one week."
    },
    {
      id: "faq-internships",
      q: "Can BizNearYou help me find internships?",
      a: "Absolutely. Many local clinics, specialized shops, local trade offices, and veterinary practices are happy to support students with local internships, but they rarely post these on massive job boards. Sourcing high-signal local businesses on BizNearYou is one of the most effective ways to locate unadvertised student internships."
    },
    {
      id: "faq-students",
      q: "Can high-school students and first-time job seekers use BizNearYou?",
      a: "Yes, the platform is custom-built for first-time job seekers, teenagers, and students. By targeting local, busy neighborhood businesses (like family dental practices, dog groomers, or local print shops), you can easily bypass the strict 'years of experience' requirements often found on massive online career portals."
    },
    {
      id: "faq-organize",
      q: "How do I save, prioritize, and organize businesses?",
      a: "Once you find a high-signal business in our explorer, you can save it to your private Dashboard with one click. From there, you can move it through your visual Pipeline (Prospect, Contacted, Followed Up, Interviewing, Hired, etc.), log private notes about decision-makers, and track your communication history securely."
    },
    {
      id: "faq-data-source",
      q: "Where does BizNearYou’s business information come from?",
      a: "Our data is compiled from public web indices, regional business directories, and open commercial registries. We collect maps, public review patterns, and website metadata to help you discover local employers and analyze their active operational footprint."
    },
    {
      id: "faq-updates",
      q: "How often is business information updated?",
      a: "Business directory details, mapping references, and public review signals are refreshed periodically from public indices to maintain accuracy. Because local business information changes rapidly, we always encourage users to verify current addresses, phone numbers, and operational status directly with each business."
    },
    {
      id: "faq-pricing",
      q: "Is BizNearYou free?",
      a: AI_FEATURES_ENABLED
        ? "Yes! BizNearYou offers a comprehensive free tier that lets you search nearby commercial targets and manage your private outreach pipeline. For power users, we also offer optional Pro memberships that unlock advanced features such as message templates, bulk CSV exports, and expanded weekly search limits."
        : "Yes! BizNearYou offers a comprehensive free tier that lets you search nearby commercial targets and manage your private outreach pipeline. For power users, we also offer optional Pro memberships that unlock advanced features such as bulk CSV exports and expanded weekly search limits."
    },
    {
      id: "faq-guarantees",
      q: "Does BizNearYou guarantee employment or an interview?",
      a: "No. BizNearYou is a research tool and CRM helper; we do not employ job seekers, act as a staffing agency, or guarantee interviews, callbacks, or employment. Your success depends entirely on your direct communication, professional presentation, and follow-up with each individual business."
    },
    {
      id: "faq-report",
      q: "How do I report incorrect business information?",
      a: "If you spot out-of-date hours, a closed location, or an incorrect website URL, you can submit a correction request via our unified Contact Us page by choosing the 'Feedback' topic. We review and update our system directory references within 48 business hours."
    },
    {
      id: "faq-removal",
      q: "How do I request that a business profile be corrected or removed?",
      a: "We take accuracy and privacy seriously. If you are a business owner and would like your business profile corrected or permanently removed from our public indexing, please go to our unified Contact page and submit a request selecting the 'Removal Request' subject option. These requests are prioritized and handled within 48 business hours."
    },
    {
      id: "faq-support",
      q: "How do I contact BizNearYou support?",
      a: "For technical help, data requests, or billing queries, please visit our unified Contact Page (available via the 'Contact Support' button in your dashboard or footer) or email our support team directly. We are a dedicated independent team and will get back to you within 24 to 48 business hours."
    },
    {
      id: "faq-affiliation",
      q: "Is BizNearYou affiliated with Google Maps, Yelp, Indeed, LinkedIn, CareerOneStop, or listed businesses?",
      a: "No. BizNearYou is a completely independent commercial research platform. We are not officially affiliated with, endorsed by, or partnered with Google Maps, Yelp, Indeed, LinkedIn, CareerOneStop, or any of the businesses listed in our search directory, unless explicitly stated otherwise. All trademark names belong to their respective owners."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      <div className="max-w-4xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "FAQ", path: "/faq" }]} onNavigate={onNavigate} />
        <div className="space-y-4 text-center md:text-left">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
            <HelpCircle className="w-3.5 h-3.5 text-blue-600" />
            <span>Help Center & FAQ</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-sm sm:text-base text-slate-500 font-medium leading-relaxed max-w-2xl">
            Everything you need to know about BizNearYou, hiring signals, job sourcing, and outreach etiquette.
          </p>
        </div>

        <div className="space-y-4 pt-4">
          {faqs.map((faq, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div
                key={faq.id}
                className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden transition-all duration-200"
              >
                <button
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                  aria-expanded={isOpen}
                  aria-controls={faq.id}
                  className="w-full text-left p-5 flex justify-between items-center gap-4 cursor-pointer hover:bg-slate-50/50"
                >
                  <span className="font-bold text-slate-900 text-sm sm:text-base leading-snug">
                    {idx + 1}. {faq.q}
                  </span>
                  <span className="text-slate-400 font-bold shrink-0 text-xl transition-transform duration-200">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>
                <div
                  id={faq.id}
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="p-5 pt-0 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-50/50 pt-4">
                      {faq.a}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl p-6 md:p-8 text-center space-y-4 shadow-sm">
          <h3 className="font-bold text-slate-900 text-base sm:text-lg">Have more questions or need profile support?</h3>
          <p className="text-xs text-slate-500 max-w-xl mx-auto leading-relaxed">
            Whether you are a job seeker looking for support, or a business owner requesting profile corrections or removal, our team is ready to assist you.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => onNavigate("/contact")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow-md transition cursor-pointer"
            >
              Contact Support
            </button>
            <button
              onClick={() => onNavigate("/")}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs px-5 py-2.5 rounded-lg transition cursor-pointer"
            >
              Back to Explorer
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 4. Find Jobs Page (/find-jobs)
export function FindJobsPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  const [activeTab, setActiveTab] = useState<"clinics" | "studios" | "gigs">("clinics");
  const [hasArbitrarySearch, setHasArbitrarySearch] = useState(false);

  // Centralized query parameter search intent resolution
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const rawQ = params.get("q") || params.get("query") || params.get("search");
    const rawLoc = params.get("loc") || params.get("location") || params.get("city");
    const rawCat = params.get("category") || params.get("cat");

    if (rawQ || rawLoc || rawCat) {
      const resolved = resolveCanonicalSearchIntent(rawQ, rawLoc, rawCat);
      if (resolved) {
        if (resolved.isStructuredIntent && resolved.canonicalPath !== "/find-jobs") {
          onNavigate(resolved.canonicalPath);
          return;
        } else if (!resolved.isStructuredIntent && rawQ) {
          setHasArbitrarySearch(true);
        }
      }
    }
  }, [onNavigate]);

  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "Find Local Employers & Hiring Signals | BizNearYou",
    description: "Discover nearby dental practices, veterinary clinics, creative agencies, and local trades with active public activity signals. Find opportunities near you.",
    canonicalUrl: "https://getbiznearyou.com/find-jobs",
    robots: hasArbitrarySearch ? "noindex, follow" : "index, follow",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Find Local Employers & Hiring Signals",
      "description": "Discover nearby clinics, agencies, and small trade offices with growing reviews and public activity.",
      "url": "https://getbiznearyou.com/find-jobs"
    }
  });

  const records = {
    clinics: [
      { name: "Apex Dental Center", location: "Chicago, IL", signal: "Active Review Velocity", reason: "Frequent patient review updates indicate high commercial activity and a potential need for administrative or front-desk help." },
      { name: "Lakeside Medical Clinic", location: "Detroit, MI", signal: "No Online Booking", reason: "An active medical staples provider with a high patient volume but manual scheduling. Perfect opportunity for an outreach offering administrative support." },
    ],
    studios: [
      { name: "Blue Sky Design Agency", location: "Austin, TX", signal: "New Service Launch", reason: "Active website updates show commercial growth. A great opportunity to pitch junior design support or copywriter services directly to the partners." },
      { name: "Vanguard Marketing Hub", location: "Denver, CO", signal: "Static Mobile Layout", reason: "Great local reputation but outdated mobile usability. Highly targeted outreach topic for proactive web design and technical students." },
    ],
    gigs: [
      { name: "Green Leaf Florals", location: "Portland, OR", signal: "Local Hub Activity", reason: "Active seasonal review boosts. Local trade operations are excellent targets for high-reliability administrative or part-time floor assistance." },
      { name: "Rover Pet Clinic & Care", location: "Miami, FL", signal: "Multi-Location Growth", reason: "Multiple branches in the same county indicate structured expansion. Excellent opportunities to secure steady hours or junior coordinator roles." },
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-white text-slate-800 py-12 px-6"
    >
      <div className="max-w-4xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "Find Jobs", path: "/find-jobs" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
            <Search className="w-3.5 h-3.5" />
            <span>Employer Sourcing Portal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Find Local Employers Worth Contacting
          </h1>
          <h2 className="text-lg text-slate-500 font-medium max-w-2xl leading-relaxed">
            Discover active clinics, agencies, and small trade offices near you. Take control of your career search with proactive research.
          </h2>
        </div>

        <p className="text-slate-600 text-sm leading-relaxed">
          Traditional job boards display stale, hyper-competitive listings from high-budget companies. BizNearYou is designed for a smarter approach: **proactive employer sourcing**. We analyze and map physical businesses in your town so you can evaluate public commercial activity and build real relationships with local decision-makers.
        </p>

        {/* Demo Tabs */}
        <div className="border border-slate-100 rounded-2xl p-6 bg-slate-50/50 space-y-6">
          <div className="space-y-2">
            <h3 className="font-bold text-slate-900 text-base sm:text-lg">Sourcing Targets & Hiring Signals</h3>
            <p className="text-xs text-slate-400">Select a category below to see how public indicators help you frame direct outreach pitches:</p>
          </div>

          <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
            {(["clinics", "studios", "gigs"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-xs font-bold rounded-lg transition capitalize cursor-pointer shrink-0 ${
                  activeTab === tab
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800"
                }`}
              >
                {tab === "gigs" ? "Part-Time & Local Gigs" : `${tab === "clinics" ? "Medical Clinics" : "Creative & Web Studios"}`}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {records[activeTab].map((item, idx) => (
              <div key={idx} className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm space-y-1.5 text-left">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <h4 className="font-black text-slate-900 text-sm">{item.name}</h4>
                  <div className="flex gap-2 items-center">
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {item.location}
                    </span>
                    <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase border border-blue-100/40">
                      {item.signal}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-505 leading-normal">
                  <span className="font-bold text-slate-700">Outreach Alignment: </span>
                  {item.reason}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 rounded-xl p-6 space-y-3.5 text-xs shadow-xs">
            <p className="font-bold text-slate-900 dark:text-white text-sm sm:text-base">Ready to search real businesses in your community?</p>
            <p className="text-slate-600 dark:text-slate-400 max-w-lg mx-auto">
              Create an account to search by city, evaluate active hiring signals, map local competitors, and start tracking your outreach milestones in our integrated CRM.
            </p>
            <button onClick={() => onLogin(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg text-xs transition cursor-pointer mt-1">
              Start Sourcing Employers
            </button>
          </div>
        </div>

        {/* Informative Article */}
        <div className="space-y-4 pt-4 border-t border-slate-100">
          <h3 className="font-black text-slate-900 text-xl">The Sourcing Advantage: Why Sourcing Beats Job Boards</h3>
          <p className="text-slate-600 text-sm leading-relaxed">
            By the time a local business registers, writes, and pays to list an opening on LinkedIn, they are already overwhelmed with robotic, automated templates. Sourcing local workplaces first allows you to introduce yourself as a thoughtful, proactive neighbor. Your message lands with the clinic owner or office manager before they feel forced to look at a job board—cutting through the noise entirely.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// 5. Hidden Jobs Page (/hidden-jobs)
export function HiddenJobsPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "Uncover Hidden Job Market Opportunities | BizNearYou",
    description: "Learn how to access unadvertised jobs in your community. Discover, prioritize, and contact local employers directly using our proactive sourcing tools.",
    canonicalUrl: "https://getbiznearyou.com/hidden-jobs",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Uncover Hidden Job Market Opportunities",
      "description": "A comprehensive guide on accessing unadvertised positions at local dental practices, design studios, and neighborhood businesses.",
      "url": "https://getbiznearyou.com/hidden-jobs"
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      <div className="max-w-4xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "Direct Outreach", path: "/hidden-jobs" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>Direct Outreach CRM</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Uncover Unadvertised Local Career Opportunities
          </h1>
          <h2 className="text-lg text-slate-500 font-medium max-w-2xl leading-relaxed">
            The best local opportunities are never posted on job boards. Learn how to locate them with proactive sourcing.
          </h2>
        </div>

        <p className="text-slate-600 text-sm leading-relaxed">
          The "hidden job market" is a reality of local commerce. Most neighborhood dental practices, boutique law offices, growing veterinary clinics, and creative agencies desperately need help, but they lack the HR teams or bandwidth to manage expensive job posts. Instead, they hire motivated candidates who reach out directly. BizNearYou gives you the sourcing tools and a private CRM pipeline to organize this outreach systematically.
        </p>

        {/* Step-by-Step Roadmap */}
        <div className="space-y-6">
          <h3 className="font-black text-slate-900 text-xl">The 4-Step Direct Outreach Blueprint</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Step 1 */}
            <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm space-y-3">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 font-bold text-sm flex items-center justify-center border border-indigo-100">1</span>
                <h4 className="font-bold text-slate-900 text-sm">Find and Map Target Employers</h4>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Start by mapping physical businesses in comfortable commuting zones on BizNearYou. Group targets by trade categories—such as private dental clinics, design studios, or accounting firms—rather than chasing generic global corporations.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm space-y-3">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 font-bold text-sm flex items-center justify-center border border-indigo-100">2</span>
                <h4 className="font-bold text-slate-900 text-sm">Evaluate Public Hiring Signals</h4>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Check active signals like customer review velocity, website age, and missing online calendars. These objective public signals highlight businesses that are busy but might have administrative bottlenecks you can help resolve.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm space-y-3">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 font-bold text-sm flex items-center justify-center border border-indigo-100">3</span>
                <h4 className="font-bold text-slate-900 text-sm">Identify Decision Makers</h4>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Find who actually manages the daily operations or owns the business. Pitching your skills directly to the lead doctor, studio partner, or office manager bypasses robotic applicant tracking databases entirely.
              </p>
            </div>

            {/* Step 4 */}
            <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm space-y-3">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 font-bold text-sm flex items-center justify-center border border-indigo-100">4</span>
                <h4 className="font-bold text-slate-900 text-sm">Log and Track in your CRM</h4>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Never contact the same business twice in a week or lose track of follow-ups. Save your researched businesses, note communication dates, and organize your proactive pipeline in our private dashboard.
              </p>
            </div>

          </div>
        </div>

        {/* Call to action card */}
        <div className="bg-indigo-50 dark:bg-indigo-950/60 text-slate-900 dark:text-white border border-indigo-200 dark:border-indigo-900/60 rounded-2xl p-6 md:p-8 text-center space-y-4 shadow-xs">
          <h3 className="font-bold text-lg md:text-xl text-indigo-950 dark:text-white">Ready to map unadvertised opportunities near you?</h3>
          <p className="text-xs md:text-sm text-indigo-800 dark:text-indigo-200 max-w-xl mx-auto">
            Create your free account today to search physical business categories by zip code, analyze public activity markers, and organize your direct outreach pipeline.
          </p>
          <button
            onClick={() => onLogin(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-6 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
          >
            Create Your CRM Account
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// 6. Local Internships Page (/local-internships)
export function LocalInternshipsPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  const [selectedTemplate, setSelectedTemplate] = useState<"marketing" | "admin" | "creative">("marketing");

  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "Secure Local Internships & Shadowing | BizNearYou",
    description: "Bypass standard job boards to find local internships, clinical hours, and shadowing roles directly. Use our research CRM to map and pitch local clinics and agencies.",
    canonicalUrl: "https://getbiznearyou.com/local-internships",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Secure Local Internships & Shadowing",
      "description": "Learn to find local internships and clinical placements in your community through direct outreach and research.",
      "url": "https://getbiznearyou.com/local-internships"
    }
  });

  const templates = {
    marketing: {
      title: "Local Studio / Agency Marketing Pitch",
      subject: "Proactive Marketing Intern Inquiry - [Your Name]",
      body: `Dear [Owner Name],

I've been following [Business Name]'s local growth, and I absolutely love the projects you showcase on Instagram. 

As a college student studying Marketing in [City], I am looking for a local internship opportunity. I specialize in filming short-form video content and organizing social media scheduling. 

I'd love to help compile a free social audit for your business to show you where we can capture more local leads. Do you have 10 minutes next week for a brief call?

Sincerely,
[Your Name]
[Your Phone Number]`
    },
    admin: {
      title: "Clinic / Medical Practice Admin Assistant Pitch",
      subject: "Inquiry: Support / Assistant Role - [Your Name]",
      body: `Dear Office Manager,

I've noticed that [Business Name] is highly rated by patients in [Neighborhood]. Having strong local veterinary/dental practices is incredibly valuable.

I am an aspiring health administrator looking to learn from a premier local practice. I am highly organized, familiar with spreadsheet automation, and fluent in digital reception. 

I'd love to pitch my help for weekend or part-time office assistance during your peak seasons. I've attached my academic resume for your convenience.

Best regards,
[Your Name]
[Your Phone Number]`
    },
    creative: {
      title: "Technical Web / Design Pitch for Retailers",
      subject: "Upgrading [Business Name]'s Web Flow - Local Web Designer",
      body: `Dear [Owner Name],

I was looking at local boutique shops in [Neighborhood] and discovered [Business Name]. Your selection is beautiful!

I noticed your website is currently missing a secure SSL certificate, which can flag a "not secure" warning to mobile shoppers. 

I'm a local freelance web developer looking to build my portfolio. I'd love to help you install an SSL certificate and clean up your mobile booking page for free as part of an academic project. Let me know if you'd be open to a quick coffee.

Sincerely,
[Your Name]
[Your Phone Number]`
    }
  };

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(templates[selectedTemplate].body);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-white text-slate-800 py-12 px-6"
    >
      <div className="max-w-4xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "Local Internships", path: "/local-internships" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 text-xs font-semibold">
            <GraduationCap className="w-3.5 h-3.5" />
            <span>Internship Sourcing Portal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Secure Local Internships via Proactive Sourcing
          </h1>
          <h2 className="text-lg text-slate-500 font-medium max-w-2xl leading-relaxed">
            Don't fight thousands of applicants on massive corporate boards. Discover, research, and contact nearby trade offices and boutique agencies directly.
          </h2>
        </div>

        <p className="text-slate-600 text-sm leading-relaxed">
          The most educational, hands-on internships exist in dynamic local businesses—small veterinary practices, independent accounting firms, specialized clinics, and boutique creative studios. These teams usually lack formal HR programs and rarely advertise open roles on Indeed. BizNearYou serves as your research hub and CRM to help you map these offices and coordinate your introductions professionally.
        </p>

        {/* Interactive Pitch Template Section */}
        <div className="border border-slate-100 rounded-2xl p-6 bg-slate-50/50 space-y-4">
          <div className="space-y-1">
            <h3 className="font-bold text-slate-900 text-base">Direct Outreach Pitch Frameworks</h3>
            <p className="text-xs text-slate-400">Select a specialization below to see how to structure a polite, value-first introduction letter:</p>
          </div>

          <div className="flex gap-2 pb-2 border-b border-slate-200 overflow-x-auto">
            {(["marketing", "admin", "creative"] as const).map((tKey) => (
              <button
                key={tKey}
                onClick={() => setSelectedTemplate(tKey)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer shrink-0 ${
                  selectedTemplate === tKey
                    ? "bg-emerald-600 text-white"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                }`}
              >
                {tKey === "marketing" ? "Social & Marketing Pitch" : tKey === "admin" ? "Administrative Pitch" : "Technical Web Pitch"}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-3 relative shadow-sm">
            <div className="flex justify-between items-center pb-2 border-b border-slate-50">
              <span className="text-[10px] font-bold text-slate-400">Outreach Email Framework</span>
              <button
                onClick={handleCopy}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1 cursor-pointer bg-slate-50 hover:bg-slate-100 px-2.5 py-1 rounded border border-slate-200 transition"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3 text-emerald-500" /> Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" /> Copy Framework
                  </>
                )}
              </button>
            </div>

            <div className="text-xs font-mono space-y-1.5 text-slate-700 leading-relaxed overflow-x-auto whitespace-pre-wrap">
              <div><strong className="text-slate-900">Subject:</strong> {templates[selectedTemplate].subject}</div>
              <div className="border-t border-slate-50 my-2"></div>
              <div>{templates[selectedTemplate].body}</div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-2xl p-6 md:p-8 text-center space-y-4 shadow-sm border border-slate-200 dark:border-slate-800">
          <h3 className="font-bold text-lg md:text-xl font-sans text-slate-900 dark:text-white">Ready to structure your internship pipeline?</h3>
          <p className="text-xs md:text-sm text-slate-600 dark:text-slate-300 max-w-xl mx-auto">
            Create an account to search physical businesses near you by category, locate verified web properties, and manage your custom outreach pipeline.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <button
              onClick={() => onNavigate("/dashboard")}
              className="bg-blue-600 hover:bg-blue-700 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Go to CRM Dashboard
            </button>
            <button
              onClick={() => onNavigate("/")}
              className="bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-white border border-slate-300 dark:border-slate-700 font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Search Local Businesses
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 7. First Jobs Page (/first-jobs)
export function FirstJobsPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "First Jobs, Gigs, & Teen Careers | BizNearYou",
    description: "Discover local small businesses ideal for entry-level work, student gigs, and part-time careers. Bypass huge job boards by reaching out to neighborhood targets.",
    canonicalUrl: "https://getbiznearyou.com/first-jobs",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "First Jobs, Gigs, & Teen Careers",
      "description": "Learn how teens and students can find local, high-reliability part-time gigs and first-time careers by identifying active neighborhood businesses.",
      "url": "https://getbiznearyou.com/first-jobs"
    }
  });

  const roles = [
    { name: "Local Commerce & Cafes", traits: "Excellent targets for high-reliability customer-facing and weekend support roles.", signalType: "Active Google review growth and regional check-ins" },
    { name: "Clinic Administrative Support", traits: "Organized, helpful demeanor on the phone, and interested in healthcare business flows.", signalType: "High patient volumes combined with manual phone booking systems" },
    { name: "Local Trade & Agency Assistant", traits: "Assisting trade shops, accounting offices, or creative firms with digital logistics or social curation.", signalType: "Frequent public review spikes with static web layouts" },
    { name: "Boutique Retail Assistant", traits: "Engaging, attentive to local patrons, and quick to organize in-store inventory.", signalType: "Newly registered commercial locations and review velocity" }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      <div className="max-w-4xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "First Jobs", path: "/first-jobs" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Early Career Sourcing</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Secure Your First Career Milestone Proactively
          </h1>
          <h2 className="text-lg text-slate-500 font-medium max-w-2xl leading-relaxed">
            Ditch generic job listings. Use proactive sourcing to discover and evaluate stable local businesses eager for reliable, entry-level candidates.
          </h2>
        </div>

        <p className="text-slate-600 text-sm leading-relaxed">
          Starting out is tough when online listing portals require three years of experience for "entry-level" jobs. BizNearYou is designed for a smarter alternative: **direct outreach**. We show you local trade offices, medical practices, and boutiques where basic reliability, coachability, and a polite introduction are valued far more than pre-existing certifications.
        </p>

        {/* Starter Jobs Tool */}
        <div className="bg-white rounded-2xl border border-slate-150 p-6 space-y-4 shadow-sm">
          <div className="space-y-1">
            <h3 className="font-bold text-slate-900 text-base">Workplaces That Value Beginners</h3>
            <p className="text-xs text-slate-400">Select a career track below to analyze what small business owners value and how to find them using our indicators:</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {roles.map((r, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedRole(r.name)}
                className={`p-4 rounded-xl text-left border cursor-pointer transition flex flex-col justify-between space-y-2 ${
                  selectedRole === r.name
                    ? "bg-blue-50 border-blue-300 text-slate-900"
                    : "bg-slate-50/50 border-slate-100 hover:border-slate-200"
                }`}
              >
                <div>
                  <h4 className="font-bold text-xs sm:text-sm text-slate-950">{r.name}</h4>
                  <p className="text-[11px] text-slate-500 mt-1 leading-normal">{r.traits}</p>
                </div>
                {selectedRole === r.name && (
                  <div className="text-[10px] text-blue-700 font-semibold bg-white p-1.5 rounded border border-blue-200 mt-2">
                    <span className="font-bold text-slate-700">Outreach alignment:</span> {r.signalType}
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className="text-center pt-2">
            <button
              onClick={() => onLogin(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs py-2.5 px-6 rounded-lg shadow-sm transition cursor-pointer inline-flex items-center gap-1.5"
            >
              Start Sourcing Employers <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Student Advice */}
        <div className="space-y-4">
          <h3 className="font-black text-slate-900 text-xl">3 Sourcing Rules for Your First Outbound Campaign</h3>
          <div className="space-y-3.5 text-sm text-slate-600">
            <p>
              <strong>1. Prioritize Review Velocity:</strong> Businesses with rapid, climbing patient or client reviews on Google are highly active. High-volume environments are where managers are most prone to feeling overwhelmed—and most receptive to part-time, helpful hands.
            </p>
            <p>
              <strong>2. Identify Operational Bottlenecks:</strong> Examine their public websites before reaching out. If you spot a great medical practice that lacks an online appointment calendar or has an outdated contact page, that is a highly specific value proposition for a polite introduction.
            </p>
            <p>
              <strong>3. Organize Your Campaign:</strong> Do not spam. Save your sourced workplaces, note down contact names, keep records of follow-ups, and organize your proactive job search in our dedicated CRM.
            </p>
          </div>
        </div>

        {/* CRM Call to Action */}
        <div className="bg-indigo-50 dark:bg-indigo-950/60 text-slate-900 dark:text-white border border-indigo-200 dark:border-indigo-900/60 rounded-2xl p-6 md:p-8 space-y-4 shadow-xs text-center mt-8">
          <h2 className="text-xl md:text-2xl font-black text-indigo-950 dark:text-white">Organize Your First Outbound Campaign</h2>
          <p className="text-xs text-indigo-800 dark:text-indigo-200 max-w-2xl mx-auto leading-relaxed">
            BizNearYou is more than just guides. It's a private tracking CRM. Search nearby businesses, evaluate public activity signals, look up contact routes, and manage your follow-ups to stay 100% organized.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <button
              onClick={() => onNavigate("/dashboard")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Go to CRM Dashboard
            </button>
            <button
              onClick={() => onNavigate("/")}
              className="bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-indigo-900 dark:text-indigo-200 border border-indigo-300 dark:border-indigo-700 font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Search Local Businesses
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 8. Businesses Hiring Near Me Page (/businesses-hiring-near-me)
export function BusinessesHiringNearMePage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "Businesses Hiring Near Me: Sourcing Signals Guide | BizNearYou",
    description: "Learn how to read high-velocity commercial activity signals—such as rapid patient reviews or static mobile layouts—to target local outbound campaigns.",
    canonicalUrl: "https://getbiznearyou.com/businesses-hiring-near-me",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Businesses Hiring Near Me: Sourcing Signals Guide",
      "description": "Understand public commercial signals and how to leverage them for proactive job-seeking and direct outreach campaigns.",
      "url": "https://getbiznearyou.com/businesses-hiring-near-me"
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-white text-slate-800 py-12 px-6"
    >
      <div className="max-w-4xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "Businesses Hiring Near Me", path: "/businesses-hiring-near-me" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Outreach Index & Signals</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Source Companies with Strong Hiring Signals
          </h1>
          <h2 className="text-lg text-slate-500 font-medium max-w-2xl leading-relaxed">
            We evaluate and catalog public business data to help you target highly active physical workplaces that stand to benefit from direct, proactive outreach.
          </h2>
        </div>

        <p className="text-slate-600 text-sm leading-relaxed">
          We do not scrape standard, competitive job ad boards. Instead, BizNearYou analyzes objective local commerce signals that indicate a workplace is busy, growing, and experiencing logistical or administrative friction. Targeting these businesses with direct outreach is the most efficient way to secure custom-tailored roles.
        </p>

        {/* Signals Explanation Card */}
        <div className="bg-slate-50 border border-slate-150 rounded-2xl p-6 sm:p-8 space-y-6">
          <h3 className="font-bold text-slate-900 text-base sm:text-lg font-sans">Public Signals of Business Activity</h3>
          
          <div className="space-y-4">
            {/* Indicator 1 */}
            <div className="flex gap-4">
              <div className="bg-white border border-slate-200 rounded-lg p-2 shrink-0 w-10 h-10 flex items-center justify-center text-indigo-600">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <p className="font-bold text-slate-900 text-sm">High Review Velocity</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  A steady stream of new patient or client feedback indicates high transaction volume. Busy operational schedules usually mean they need administrative support or part-time help.
                </p>
              </div>
            </div>

            {/* Indicator 2 */}
            <div className="flex gap-4">
              <div className="bg-white border border-slate-200 rounded-lg p-2 shrink-0 w-10 h-10 flex items-center justify-center text-blue-600">
                <Lock className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <p className="font-bold text-slate-900 text-sm">Static / Slow Web Presences</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  An established provider with excellent feedback but an outdated, non-responsive mobile website. This is a perfect open-door pitch for freelance web designers or tech-savvy students.
                </p>
              </div>
            </div>

            {/* Indicator 3 */}
            <div className="flex gap-4">
              <div className="bg-white border border-slate-200 rounded-lg p-2 shrink-0 w-10 h-10 flex items-center justify-center text-emerald-600">
                <HelpCircle className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <p className="font-bold text-slate-900 text-sm">No Online Calendars or Booking</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                   Salons, dental clinics, or auto mechanics operating without automated web scheduling experience substantial scheduling bottlenecks. Offering to deploy a simple scheduler is an immediate value-add.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Warning / Compliance check */}
        <div className="border border-amber-200 bg-amber-50/50 p-4 rounded-xl text-xs space-y-1">
          <p className="font-bold text-amber-900 flex items-center gap-1">
            <Info className="w-4 h-4 shrink-0 text-amber-700" /> Disclaimer on Sourcing Signals:
          </p>
          <p className="text-amber-800 leading-relaxed">
            These public commercial signals guide your proactive employer research. Sourcing active workplaces bypasses standard corporate channels, but we do not guarantee immediate vacancies. Our purpose is to help you map, evaluate, and contact local decision-makers to land your own custom-tailored roles.
          </p>
        </div>

        <div className="text-center">
          <button
            onClick={() => onLogin(true)}
            className="bg-indigo-650 hover:bg-indigo-700 text-white font-bold text-xs py-3 px-8 rounded-lg shadow-md transition cursor-pointer"
          >
            Start Sourcing Employers
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// 9. How It Works Page (/how-it-works)
export function HowItWorksPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "How BizNearYou Works: 4 Steps to Proactive Job Sourcing",
    description: "Learn how to discover local small businesses, analyze commercial activity signals, and coordinate outbound networking with our private outreach CRM.",
    canonicalUrl: "https://getbiznearyou.com/how-it-works",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "How BizNearYou Works: 4 Steps to Proactive Job Sourcing",
      "description": "Learn to discover, score, and contact local employers in 4 simple structured steps.",
      "url": "https://getbiznearyou.com/how-it-works"
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      <div className="max-w-4xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "How It Works", path: "/how-it-works" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Platform Guide</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            How BizNearYou works
          </h1>
          <h2 className="text-lg text-slate-500 font-medium max-w-2xl leading-relaxed">
            A simple approach to finding unadvertised local work.
          </h2>
        </div>

        <p className="text-slate-600 text-sm leading-relaxed">
          BizNearYou is a simple tool to help you find nearby workplaces, look up their details, and keep track of who you've introduced yourself to.
        </p>

        {/* Visual Map Timeline */}
        <div className="relative border-l-2 border-slate-200 pl-6 ml-4 space-y-8">
          
          {/* Step 1 */}
          <div className="relative">
            <span className="absolute -left-10 top-0.5 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs ring-4 ring-slate-50">1</span>
            <div className="space-y-1.5">
              <h4 className="font-bold text-slate-900 text-sm">Choose your area</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Type in your town or zip code. We'll show you physical businesses nearby, like clinics, design agencies, studios, and retailers.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="relative">
            <span className="absolute -left-10 top-0.5 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs ring-4 ring-slate-50">2</span>
            <div className="space-y-1.5">
              <h4 className="font-bold text-slate-900 text-sm">Look at public signals</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                See which businesses have active reviews, simple or outdated websites, or missing booking flows so you can offer specific help.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="relative">
            <span className="absolute -left-10 top-0.5 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs ring-4 ring-slate-50">3</span>
            <div className="space-y-1.5">
              <h4 className="font-bold text-slate-900 text-sm">Keep track of your outreach</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Keep a private list of places you like, and note when you've emailed, called, or dropped by so you never lose track.
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="relative">
            <span className="absolute -left-10 top-0.5 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs ring-4 ring-slate-50">4</span>
            <div className="space-y-1.5">
              <h4 className="font-bold text-slate-900 text-sm">Keep up a steady habit</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Finding a starting role is a numbers game. Setting a simple daily routine of researching a few new businesses helps keep your search moving forward.
              </p>
            </div>
          </div>

        </div>

        {/* CTA */}
        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8 text-center space-y-4 shadow-xs">
          <h3 className="font-bold text-base sm:text-lg">Ready to start looking?</h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 max-w-xl mx-auto leading-relaxed">
            Introducing yourself to active teams is a great way to find a role before anyone else does.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <button
              onClick={() => onNavigate("/dashboard")}
              className="bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Go to CRM Dashboard
            </button>
            <button
              onClick={() => onNavigate("/")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Search Local Businesses
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 10. Direct Contact / Outreach Guide Page (/how-to-contact-local-businesses-for-a-job)
export function HowToContactBusinessesPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "How to Contact Local Businesses for a Job: Sourcing Etiquette",
    description: "Discover a step-by-step guide to contacting neighborhood businesses for unadvertised jobs. Access high-performing email templates, phone scripts, and tracking tips.",
    canonicalUrl: "https://getbiznearyou.com/how-to-contact-local-businesses-for-a-job",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "How to Contact Local Businesses for a Job: Sourcing Etiquette",
      "description": "Learn to respectfully initiate contact with local dental, veterinary, or retail businesses to inquire about helper roles.",
      "url": "https://getbiznearyou.com/how-to-contact-local-businesses-for-a-job"
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      <div className="max-w-4xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "Guides", path: "/guides" }, { label: "How to Contact Local Businesses", path: "/how-to-contact-local-businesses-for-a-job" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
            <Bookmark className="w-3.5 h-3.5 text-blue-600" />
            <span>Direct Outreach Guide</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            How to Contact Local Businesses for a Job
          </h1>
          <h2 className="text-lg text-slate-500 font-medium max-w-2xl leading-relaxed">
            Stop waiting for crowded job postings. Learn how to reach out directly to physical businesses in your community and discover the hidden job market.
          </h2>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm space-y-4">
          <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-500" /> Important Sourcing Disclaimer
          </h3>
          <p className="text-xs text-slate-600 leading-relaxed">
            A hiring signal detected on BizNearYou is <strong>not a guaranteed open job vacancy</strong>. It indicates that the business is growing, active, hiring in other locations, or displaying solid indicators of commercial activity. Direct outreach is about starting a professional conversation, displaying initiative, and asking if they have any unadvertised needs or future openings.
          </p>
        </div>

        <div className="space-y-6">
          <h3 className="text-xl font-bold text-slate-900">Step 1: Do Your Research First</h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            Before picking up the phone or typing an email, research what the business actually does. Look at their website, check their social media, and read their services. Identify the owner or a team lead by name if possible. Customized, personal messages are infinitely more successful than copy-pasted templates.
          </p>

          <h3 className="text-xl font-bold text-slate-900">Step 2: Use Professional Email Templates</h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            Keep your email concise, polite, and focused on what you can offer. Here is a high-performing outreach email template you can adapt:
          </p>
          
          <div className="bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-800 rounded-xl p-5 font-mono text-xs space-y-3 relative overflow-hidden">
            <div className="absolute top-3 right-3 text-[10px] text-slate-600 dark:text-slate-400 bg-slate-200 dark:bg-slate-800 px-2 py-0.5 rounded font-bold">TEMPLATE</div>
            <p><strong>Subject:</strong> Inquiry: Student Internships / Part-Time Opportunities at [Company Name]</p>
            <p>Dear [Manager or Owner Name],</p>
            <p>I hope this email finds you well.</p>
            <p>My name is [Your Name], and I am a local student/resident living nearby in [City/Neighborhood]. I have been following [Company Name]'s local work and am very impressed by your [mention a specific service, project, or review].</p>
            <p>I am currently looking for a [part-time job / internship / helper role] and wanted to reach out to see if you have any unadvertised needs or upcoming opportunities on your team. I have experience in [mention 1-2 skills, e.g., customer service, digital marketing, organizing, basic coding] and would love to contribute to your work.</p>
            <p>I have attached my short resume for your reference. Would you be open to a brief 5-minute phone call sometime next week to see if my background aligns with any future needs?</p>
            <p>Thank you for your time and consideration.</p>
            <p>Best regards,</p>
            <p>[Your Name]<br />[Your Phone Number]<br />[Your LinkedIn Profile Link]</p>
          </div>

          <h3 className="text-xl font-bold text-slate-900">Step 3: Keep a Phone Script Handy</h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            If you call a business, speak clearly and politely. Ask for the manager or owner, but do not be pushy. Respect their time above all else. Here is a simple script:
          </p>

          <div className="bg-slate-100 border border-slate-200 text-slate-800 rounded-lg p-5 font-mono text-xs space-y-3">
            <p><strong>Greeting:</strong> "Hello! My name is [Your Name]. I'm a local student/resident nearby in [City]."</p>
            <p><strong>The Ask:</strong> "I'm looking for a local [internship/entry-level job] and was wondering if I could speak briefly with the owner or general manager, if they have a minute?"</p>
            <p><strong>If they are busy:</strong> "I understand you're busy! Would it be better if I sent a quick email, or is there a good time to call back?"</p>
            <p><strong>If they are available:</strong> "Thank you! I'm a big fan of your business. I'm looking to apply my skills in [your specialty] and wanted to know if you ever take on local helpers, part-timers, or interns. I'd love to drop off my resume or send over a quick email with my details."</p>
          </div>

          <h3 className="text-xl font-bold text-slate-900">Step 4: Keep Track in your CRM</h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            Finding a starting job is a structured habit. Never contact the same business twice in a week. Use BizNearYou's built-in tracking features to note the date, method of outreach (email, call, in-person), and any response you receive. This ensures a highly organized and professional search process.
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8 text-center space-y-4 shadow-xs">
          <h3 className="font-bold text-base sm:text-lg">Track your outreach on BizNearYou</h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 max-w-xl mx-auto leading-relaxed">
            Save local businesses, check their contact options, and manage your status from a private CRM workspace.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <button
              onClick={() => onNavigate("/dashboard")}
              className="bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Go to CRM Dashboard
            </button>
            <button
              onClick={() => onNavigate("/")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow transition cursor-pointer"
            >
              Search Local Businesses
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 11. Privacy Policy Page (/privacy)
export function PrivacyPage({ onNavigate }: Omit<PublicPageProps, "currentPath">) {
  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "Privacy Policy | BizNearYou",
    description: "Read the Privacy Policy of BizNearYou to understand how we secure your data, search queries, and local outreach CRM logs.",
    canonicalUrl: "https://getbiznearyou.com/privacy"
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      <div className="max-w-3xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "Privacy Policy", path: "/privacy" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
            <span>Transparency Center</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-xs text-slate-500">Last updated: July 13, 2026</p>
        </div>

        <div className="prose prose-slate prose-sm max-w-none space-y-6 text-sm text-slate-600 leading-relaxed">
          <p>
            At BizNearYou, we take your privacy seriously. This Privacy Policy describes how we collect, use, and handle your information when you use our local business discovery tools and job seeker CRM.
          </p>

          <h3 className="text-lg font-bold text-slate-900">1. Information We Collect</h3>
          <p>
            When you create an account, we collect your email address and password credentials securely. If you choose to log in with Google, we collect your basic profile details as authorized. Inside the CRM, we store your search queries, saved businesses, and your custom tracking notes so you can access them across your devices.
          </p>

          <h3 className="text-lg font-bold text-slate-900">2. How We Use Information</h3>
          <p>
            We use your information strictly to provide, customize, and secure the BizNearYou services. This includes saving your CRM records, rendering personalized scores for local businesses, and providing location-specific search filters. We do NOT sell your data, nor do we share it with third parties for marketing purposes.
          </p>

          <h3 className="text-lg font-bold text-slate-900">3. Sourced Business Data</h3>
          <p>
            BizNearYou displays information about physical, public businesses. This data is aggregated from public maps, business directories, and publicly accessible web signals. No secure, private, or credential-locked company data is ever displayed. If you are a business owner and would like to update your information, request removal, or claim your profile, please use our contact form or contact us at support@getbiznearyou.com.
          </p>

          <h3 className="text-lg font-bold text-slate-900">4. Your Data Control Rights</h3>
          <p>
            You have full control over your data. You can export your saved businesses, delete individual CRM notes, or completely delete your account at any time from your account settings page. Deleting your account will permanently erase your personal data from our servers.
          </p>

          <h3 className="text-lg font-bold text-slate-900">5. Contact and Removal Requests</h3>
          <p>
            If you have any questions about this Privacy Policy, or if you would like to request immediate data removal or profile correction, please contact our privacy officer via our <button onClick={() => onNavigate("/contact")} className="text-blue-600 hover:underline font-medium cursor-pointer">Contact Page</button> or email us at support@getbiznearyou.com.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// 12. Terms of Service Page (/terms)
export function TermsPage({ onNavigate }: Omit<PublicPageProps, "currentPath">) {
  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "Terms of Service | BizNearYou",
    description: "Read our Terms of Service to understand the guidelines for professional local outreach and sourcing on the BizNearYou platform.",
    canonicalUrl: "https://getbiznearyou.com/terms"
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      <div className="max-w-3xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "Terms of Service", path: "/terms" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
            <FileText className="w-3.5 h-3.5 text-blue-600" />
            <span>Platform Guidelines</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Terms of Service
          </h1>
          <p className="text-xs text-slate-500">Last updated: July 13, 2026</p>
        </div>

        <div className="prose prose-slate prose-sm max-w-none space-y-6 text-sm text-slate-600 leading-relaxed">
          <p>
            Welcome to BizNearYou. By accessing or using our website, services, and local career tools, you agree to comply with and be bound by the following Terms of Service.
          </p>

          <h3 className="text-lg font-bold text-slate-900">1. Sourcing and Accuracy Disclaimer</h3>
          <p>
            BizNearYou provides local business signals, scores, and outreach tracking tools. All signals (such as hiring likelihoods, business health indicators, or vacancy scores) are generated based on public directory heuristics, rating counts, website features, and general local indicators. They are meant as research guides only. We do not guarantee that any business is actively hiring, has a current open vacancy, or will respond to your outreach.
          </p>

          <h3 className="text-lg font-bold text-slate-900">2. Permitted Use of the Platform</h3>
          <p>
            BizNearYou is intended solely for personal, non-commercial use by individual job seekers, students, and local outreach researchers. You are strictly forbidden from scraping, bulk-downloading, or extracting business profiles for commercial directories or competing applications.
          </p>

          <h3 className="text-lg font-bold text-slate-900">3. Professional Outreach Standards</h3>
          <p>
            Our tool helps you organize contact information to introduce yourself. Users must adhere to professional, respectful standards when contacting local businesses. You must comply with all local, state, and federal regulations regarding telephone calls, commercial emails (such as CAN-SPAM), and privacy rules. BizNearYou is not responsible for any communications between users and third-party businesses.
          </p>

          <h3 className="text-lg font-bold text-slate-900">4. Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, BizNearYou, its founders, and affiliates will not be liable for any indirect, incidental, special, or consequential damages resulting from your use of the platform, your outreach efforts, or any employment outcomes.
          </p>

          <h3 className="text-lg font-bold text-slate-900">5. Governing Law</h3>
          <p>
            These terms are governed by and construed in accordance with the laws of your local jurisdiction, without regard to its conflict of law principles. If you have any inquiries, please contact us at support@getbiznearyou.com.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// 13. Contact Page (/contact)
export function ContactPage({ 
  onNavigate, 
  currentUser, 
  initialSubject = ""
}: Omit<PublicPageProps, "currentPath"> & { 
  currentUser?: any; 
  initialSubject?: string;
}) {
  const mapSubjectToType = (subject: string): ContactSubmissionType => {
    const s = subject.toLowerCase().trim();
    if (s.includes("bug")) return "bug_report";
    if (s.includes("feedback")) return "feedback";
    if (s.includes("removal") || s.includes("opt-out")) return "removal_request";
    if (s.includes("feature")) return "feature_request";
    return "general_inquiry";
  };

  const [submissionType, setSubmissionType] = useState<ContactSubmissionType>(mapSubjectToType(initialSubject));
  const [formData, setFormData] = useState({
    name: currentUser?.name || currentUser?.displayName || "",
    email: currentUser?.email || "",
    businessUrl: "",
    relationshipToBusiness: "" as RemovalRelationship | "",
    supportingInfo: "",
    bugBrowser: "",
    bugDevice: "",
    bugSteps: "",
    message: "",
    honeypot: ""
  });

  const [submitted, setSubmitted] = useState(false);
  const [submissionResultMessage, setSubmissionResultMessage] = useState("");
  const [isAnonymousSubmission, setIsAnonymousSubmission] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [announcement, setAnnouncement] = useState("");

  const activeConfig = CONTACT_TYPE_CONFIG[submissionType] || CONTACT_TYPE_CONFIG.general_inquiry;

  useEffect(() => {
    if (initialSubject) {
      setSubmissionType(mapSubjectToType(initialSubject));
    }
  }, [initialSubject]);

  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        name: prev.name || currentUser.name || currentUser.displayName || "",
        email: prev.email || currentUser.email || ""
      }));
    }
  }, [currentUser]);

  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "Contact Us & Business Corrections | BizNearYou",
    description: "Get in touch with the BizNearYou team for general support, feedback, feature requests, or listing removal requests.",
    canonicalUrl: "https://getbiznearyou.com/contact"
  });

  const handleTypeChange = (newType: ContactSubmissionType) => {
    setSubmissionType(newType);
    setError(null);
    setFieldErrors({});
    const newConfig = CONTACT_TYPE_CONFIG[newType];
    setAnnouncement(`Form updated for ${newConfig.label}. ${newConfig.description}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    const clientPayload = {
      submissionType,
      name: formData.name,
      email: formData.email,
      businessUrl: formData.businessUrl,
      relationshipToBusiness: formData.relationshipToBusiness,
      supportingInfo: formData.supportingInfo,
      bugBrowser: formData.bugBrowser,
      bugDevice: formData.bugDevice,
      bugSteps: formData.bugSteps,
      message: formData.message,
      honeypot: formData.honeypot
    };

    const valResult = validateContactSubmission(clientPayload);

    if (valResult.error) {
      setLoading(false);
      setError(valResult.error);
      if (valResult.fieldErrors) {
        setFieldErrors(valResult.fieldErrors);
        const firstField = Object.keys(valResult.fieldErrors)[0];
        if (firstField) {
          const fieldIdMap: Record<string, string> = {
            submissionType: "contact-submission-type",
            name: "contact-name",
            email: "contact-email",
            businessUrl: "contact-business-url",
            relationshipToBusiness: "contact-relationship",
            message: "contact-message",
            bugSteps: "contact-bug-steps"
          };
          const targetId = fieldIdMap[firstField] || "contact-message";
          setTimeout(() => {
            const elem = document.getElementById(targetId);
            if (elem) elem.focus();
          }, 50);
        }
      }
      return;
    }

    try {
      const result = await fetchJsonWithRetry("/api/support/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(clientPayload),
      });

      setLoading(false);
      setSubmitted(true);
      setIsAnonymousSubmission(Boolean(result?.isAnonymous));
      setSubmissionResultMessage(
        result.message ||
        (result.isAnonymous
          ? "Your submission was received. Because no email was provided, BizNearYou will not be able to reply."
          : "Your submission was received. BizNearYou may contact you at the email provided.")
      );

      // Reset form fields
      setFormData({
        name: "",
        email: "",
        businessUrl: "",
        relationshipToBusiness: "",
        supportingInfo: "",
        bugBrowser: "",
        bugDevice: "",
        bugSteps: "",
        message: "",
        honeypot: ""
      });
    } catch (err: any) {
      setLoading(false);
      setError(err.message || "An error occurred while sending your request.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      {/* Screen reader announcement for dynamic changes */}
      <div className="sr-only" aria-live="polite">{announcement}</div>

      <div className="max-w-3xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "Contact Us", path: "/contact" }]} onNavigate={onNavigate} />
        <div className="space-y-3">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
            <Mail className="w-3.5 h-3.5 text-blue-600" />
            <span>Support and Inquiry Center</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Contact BizNearYou
          </h1>
          <p className="text-sm text-slate-500 max-w-xl leading-relaxed">
            Submit general questions, report technical issues, share feedback, or request listing updates. Choose a request category to view relevant requirements.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Form container */}
          <div className="md:col-span-2 bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm">
            {submitted ? (
              <div className="py-8 text-center space-y-4">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Submission Received</h3>
                <p className="text-xs text-slate-600 leading-relaxed max-w-sm mx-auto p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                  {submissionResultMessage}
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:underline cursor-pointer"
                >
                  <span>Submit another inquiry</span> <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {error && (
                  <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-start gap-2.5">
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold block">Submission requirement not met</span>
                      <span className="text-[11.5px] leading-relaxed">{error}</span>
                    </div>
                  </div>
                )}

                {/* Inquiry Category Selector */}
                <div className="space-y-1.5">
                  <label htmlFor="contact-submission-type" className="text-xs font-bold text-slate-800 flex items-center gap-1">
                    Inquiry Category <span className="text-rose-500">*</span>
                  </label>
                  <select
                    id="contact-submission-type"
                    value={submissionType}
                    onChange={(e) => handleTypeChange(e.target.value as ContactSubmissionType)}
                    aria-describedby="contact-type-helper"
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-slate-900"
                  >
                    <option value="general_inquiry">General Inquiry</option>
                    <option value="bug_report">Bug Report</option>
                    <option value="feedback">Feedback / Suggestion</option>
                    <option value="feature_request">Feature Request</option>
                    <option value="removal_request">Profile / Listing Removal Request</option>
                  </select>
                  <p id="contact-type-helper" className="text-[11px] text-slate-500 flex items-center gap-1 mt-1">
                    <Info className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                    <span>{activeConfig.description}</span>
                  </p>
                </div>

                {/* Name Field */}
                {activeConfig.fields.name.visible && (
                  <div className="space-y-1">
                    <label htmlFor="contact-name" className="text-xs font-bold text-slate-800">
                      {activeConfig.fields.name.label}
                      {activeConfig.fields.name.required ? (
                        <span className="text-rose-500 ml-0.5">*</span>
                      ) : (
                        <span className="text-slate-400 font-normal text-[11px] ml-1">(Optional)</span>
                      )}
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Alex Morgan"
                      aria-describedby={fieldErrors.name ? "contact-name-error" : "contact-name-helper"}
                      className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                        fieldErrors.name ? "border-rose-300 ring-1 ring-rose-300" : "border-slate-200"
                      }`}
                    />
                    {fieldErrors.name ? (
                      <p id="contact-name-error" className="text-[11px] text-rose-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0" /> {fieldErrors.name}
                      </p>
                    ) : (
                      <p id="contact-name-helper" className="text-[10.5px] text-slate-400">
                        {activeConfig.fields.name.helperText}
                      </p>
                    )}
                  </div>
                )}

                {/* Email Field */}
                {activeConfig.fields.email.visible && (
                  <div className="space-y-1">
                    <label htmlFor="contact-email" className="text-xs font-bold text-slate-800">
                      {activeConfig.fields.email.label}
                      {activeConfig.fields.email.required ? (
                        <span className="text-rose-500 ml-0.5">*</span>
                      ) : (
                        <span className="text-slate-400 font-normal text-[11px] ml-1">(Optional for anonymous report)</span>
                      )}
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="e.g., alex@example.com"
                      aria-describedby={fieldErrors.email ? "contact-email-error" : "contact-email-helper"}
                      className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                        fieldErrors.email ? "border-rose-300 ring-1 ring-rose-300" : "border-slate-200"
                      }`}
                    />
                    {fieldErrors.email ? (
                      <p id="contact-email-error" className="text-[11px] text-rose-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0" /> {fieldErrors.email}
                      </p>
                    ) : (
                      <p id="contact-email-helper" className="text-[10.5px] text-slate-400">
                        {activeConfig.fields.email.helperText}
                      </p>
                    )}
                  </div>
                )}

                {/* Business URL Field */}
                {activeConfig.fields.businessUrl.visible && (
                  <div className="space-y-1">
                    <label htmlFor="contact-business-url" className="text-xs font-bold text-slate-800">
                      {activeConfig.fields.businessUrl.label}
                      {activeConfig.fields.businessUrl.required ? (
                        <span className="text-rose-500 ml-0.5">*</span>
                      ) : (
                        <span className="text-slate-400 font-normal text-[11px] ml-1">(Optional)</span>
                      )}
                    </label>
                    <input
                      id="contact-business-url"
                      type="url"
                      value={formData.businessUrl}
                      onChange={(e) => setFormData({ ...formData, businessUrl: e.target.value })}
                      placeholder={activeConfig.fields.businessUrl.placeholder || "https://getbiznearyou.com/?sharedBusinessId=ChIJ..."}
                      aria-describedby={fieldErrors.businessUrl ? "contact-business-url-error" : "contact-business-url-helper"}
                      className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-sans ${
                        fieldErrors.businessUrl ? "border-rose-300 ring-1 ring-rose-300" : "border-slate-200"
                      }`}
                    />
                    {fieldErrors.businessUrl ? (
                      <p id="contact-business-url-error" className="text-[11px] text-rose-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0" /> {fieldErrors.businessUrl}
                      </p>
                    ) : (
                      <p id="contact-business-url-helper" className="text-[10.5px] text-slate-400 font-sans">
                        {activeConfig.fields.businessUrl.helperText}
                      </p>
                    )}
                  </div>
                )}

                {/* Removal Request Relationship Field */}
                {activeConfig.fields.relationship?.visible && (
                  <div className="space-y-1">
                    <label htmlFor="contact-relationship" className="text-xs font-bold text-slate-800">
                      {activeConfig.fields.relationship.label}
                      {activeConfig.fields.relationship.required && <span className="text-rose-500 ml-0.5">*</span>}
                    </label>
                    <select
                      id="contact-relationship"
                      value={formData.relationshipToBusiness}
                      onChange={(e) => setFormData({ ...formData, relationshipToBusiness: e.target.value as RemovalRelationship })}
                      aria-describedby={fieldErrors.relationshipToBusiness ? "contact-relationship-error" : "contact-relationship-helper"}
                      className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                        fieldErrors.relationshipToBusiness ? "border-rose-300 ring-1 ring-rose-300" : "border-slate-200"
                      }`}
                    >
                      <option value="">Please select your relationship...</option>
                      {RELATIONSHIP_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.relationshipToBusiness ? (
                      <p id="contact-relationship-error" className="text-[11px] text-rose-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0" /> {fieldErrors.relationshipToBusiness}
                      </p>
                    ) : (
                      <p id="contact-relationship-helper" className="text-[10.5px] text-slate-400">
                        {activeConfig.fields.relationship.helperText}
                      </p>
                    )}
                  </div>
                )}

                {/* Removal Notice Warning */}
                {submissionType === "removal_request" && (
                  <div className="p-3.5 bg-amber-50/90 border border-amber-200 rounded-xl text-xs text-amber-900 space-y-1">
                    <div className="font-bold flex items-center gap-1.5 text-amber-900">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Identity & Verification Warning</span>
                    </div>
                    <p className="text-[11.5px] leading-relaxed text-amber-800">
                      Submitting a removal request does <strong>NOT</strong> automatically remove a listing. BizNearYou may verify your identity and relationship before taking action. False or fraudulent requests may be rejected.
                    </p>
                  </div>
                )}

                {/* Supporting Info Field (Optional for Removal) */}
                {activeConfig.fields.supportingInfo?.visible && (
                  <div className="space-y-1">
                    <label htmlFor="contact-supporting-info" className="text-xs font-bold text-slate-800">
                      {activeConfig.fields.supportingInfo.label}
                      <span className="text-slate-400 font-normal text-[11px] ml-1">(Optional)</span>
                    </label>
                    <textarea
                      id="contact-supporting-info"
                      rows={2}
                      value={formData.supportingInfo}
                      onChange={(e) => setFormData({ ...formData, supportingInfo: e.target.value })}
                      placeholder="e.g., Business registration link, official email domain, or reason for request"
                      aria-describedby="contact-supporting-info-helper"
                      className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                    <p id="contact-supporting-info-helper" className="text-[10.5px] text-slate-400">
                      {activeConfig.fields.supportingInfo.helperText}
                    </p>
                  </div>
                )}

                {/* Bug Report Optional Diagnostics */}
                {submissionType === "bug_report" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-slate-50 border border-slate-200/80 rounded-xl">
                    <div className="space-y-1">
                      <label htmlFor="contact-bug-browser" className="text-[11px] font-bold text-slate-700">
                        Browser / OS <span className="text-slate-400 font-normal">(Optional)</span>
                      </label>
                      <input
                        id="contact-bug-browser"
                        type="text"
                        value={formData.bugBrowser}
                        onChange={(e) => setFormData({ ...formData, bugBrowser: e.target.value })}
                        placeholder="e.g., Chrome 122 on macOS"
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="contact-bug-device" className="text-[11px] font-bold text-slate-700">
                        Device Type <span className="text-slate-400 font-normal">(Optional)</span>
                      </label>
                      <input
                        id="contact-bug-device"
                        type="text"
                        value={formData.bugDevice}
                        onChange={(e) => setFormData({ ...formData, bugDevice: e.target.value })}
                        placeholder="e.g., iPhone 15 Pro, Desktop PC"
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white"
                      />
                    </div>
                    <div className="sm:col-span-2 space-y-1">
                      <label htmlFor="contact-bug-steps" className="text-[11px] font-bold text-slate-700">
                        Steps to Reproduce <span className="text-slate-400 font-normal">(Optional)</span>
                      </label>
                      <textarea
                        id="contact-bug-steps"
                        rows={2}
                        value={formData.bugSteps}
                        onChange={(e) => setFormData({ ...formData, bugSteps: e.target.value })}
                        placeholder="1. Navigated to search, 2. Filtered by category, 3. Observed blank screen"
                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white"
                      />
                    </div>
                  </div>
                )}

                {/* Message Field */}
                <div className="space-y-1">
                  <label htmlFor="contact-message" className="text-xs font-bold text-slate-800">
                    {activeConfig.fields.message.label} <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder={
                      submissionType === "bug_report"
                        ? "Describe what happened, expected behavior, and any error message..."
                        : submissionType === "removal_request"
                        ? "Please state why you are requesting removal of this business listing..."
                        : "Type your message details here (minimum 10 characters)..."
                    }
                    aria-describedby={fieldErrors.message ? "contact-message-error" : "contact-message-helper"}
                    className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
                      fieldErrors.message ? "border-rose-300 ring-1 ring-rose-300" : "border-slate-200"
                    }`}
                  />
                  {fieldErrors.message ? (
                    <p id="contact-message-error" className="text-[11px] text-rose-600 font-medium flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 shrink-0" /> {fieldErrors.message}
                    </p>
                  ) : (
                    <p id="contact-message-helper" className="text-[10.5px] text-slate-400">
                      {activeConfig.fields.message.helperText}
                    </p>
                  )}
                </div>

                {/* Privacy & Security Disclosure Box */}
                <div className="p-4 rounded-xl bg-slate-100/90 border border-slate-200 text-xs text-slate-600 space-y-2">
                  <div className="font-bold text-slate-900 flex items-center gap-1.5">
                    <ShieldAlert className="w-4 h-4 text-blue-600 shrink-0" />
                    <span>Privacy & Security Notice</span>
                  </div>
                  <p className="leading-relaxed">
                    Please do <strong>NOT</strong> include sensitive information such as passwords, banking details, payment card numbers, Social Security numbers, or government identification details.
                  </p>
                  <p className="leading-relaxed">
                    Your information will only be used to process your request in accordance with our{" "}
                    <button
                      type="button"
                      onClick={() => onNavigate("/privacy")}
                      className="text-blue-600 font-semibold hover:underline cursor-pointer"
                    >
                      Privacy Policy
                    </button>.
                  </p>
                  {!activeConfig.fields.email.required && (
                    <p className="text-slate-500 text-[11px] italic pt-1 border-t border-slate-200/60">
                      You may submit this form anonymously without an email address. Without an email, we cannot reply directly to your submission.
                    </p>
                  )}
                </div>

                {/* Honey pot bot protection */}
                <div style={{ display: "none" }} aria-hidden="true">
                  <label htmlFor="contact-honeypot">Leave empty</label>
                  <input
                    id="contact-honeypot"
                    type="text"
                    value={formData.honeypot}
                    onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-bold text-xs py-2.5 rounded-lg shadow-sm transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  {loading ? "Submitting..." : "Submit Inquiry"} <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </form>
            )}
          </div>

          {/* Quick Info Sidebar */}
          <div className="space-y-5 text-xs text-slate-500 self-start md:mt-2">
            {activeConfig.fields.businessUrl.visible && (
              <div className="p-4 rounded-xl bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/90 dark:border-slate-700/80 space-y-2 text-xs font-sans text-left">
                <h4 className="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5 text-xs">
                  <LinkIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <span>How to copy a business link</span>
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-slate-600 dark:text-slate-300 text-[11.5px] font-medium leading-relaxed">
                  <li>Search for the business in BizNearYou.</li>
                  <li>Click View Details on the correct business.</li>
                  <li>Click Share Business.</li>
                  <li>Click Copy Link, then paste the link here.</li>
                </ol>
                <div className="text-[10.5px] text-slate-500 dark:text-slate-400 pt-1.5 border-t border-slate-200/80 dark:border-slate-700/80">
                  <span>The copied link should look similar to:</span>
                  <div className="mt-1">
                    <code className="text-blue-600 dark:text-blue-400 font-mono text-[11px] bg-blue-50 dark:bg-blue-950/60 px-1.5 py-0.5 rounded border border-blue-200/60 dark:border-blue-800/60 inline-block select-all">
                      https://getbiznearyou.com/?sharedBusinessId=ChIJ...
                    </code>
                  </div>
                </div>
              </div>
            )}
            <div className="p-4 rounded-xl bg-slate-100 border border-slate-200 space-y-2">
              <h4 className="font-bold text-slate-900 text-xs">Listing Removal Policy</h4>
              <p className="leading-relaxed text-[11.5px]">
                BizNearYou verifies business owner identities before processing removal requests. Selecting "Profile / Listing Removal Request" guides you through the necessary information required for processing.
              </p>
            </div>
            <div className="p-4 rounded-xl bg-slate-100 border border-slate-200 space-y-2">
              <h4 className="font-bold text-slate-900 text-xs">Direct Support</h4>
              <p className="leading-relaxed text-[11.5px]">
                For security inquiries or legal requests, contact our compliance team directly:
              </p>
              <p className="font-bold text-slate-800">support@getbiznearyou.com</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 14. About Page (/about)
export function AboutPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "About BizNearYou: Local Career Sourcing Tools & Values",
    description: "Learn about the mission behind BizNearYou: empowering proactive job seekers, teens, and students to access the local hidden job market.",
    canonicalUrl: "https://getbiznearyou.com/about"
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 text-slate-800 py-12 px-6"
    >
      <div className="max-w-3xl mx-auto text-left space-y-8">
        <SeoBreadcrumbs items={[{ label: "About Us", path: "/about" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">
            <Compass className="w-3.5 h-3.5 text-blue-600" />
            <span>Our Sourcing & Values</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            About BizNearYou
          </h1>
          <h2 className="text-lg text-slate-500 font-medium max-w-2xl leading-relaxed">
            Empowering proactive job seekers to discover and connect with local workplaces.
          </h2>
        </div>

        <div className="prose prose-slate prose-sm text-sm text-slate-600 leading-relaxed space-y-6">
          <p>
            Traditional job boards are crowded, expensive, and capture only a fraction of the actual work available in your community. Most local clinics, offices, dental practices, design studios, and retail shops hire through local word-of-mouth or direct contact—long before they ever pay to post a listing online. This is the <strong>hidden job market</strong>.
          </p>
          <p>
            BizNearYou was founded to level the playing field. We built a local discovery engine and job seeker CRM that makes it easy for students, early-career seekers, and proactive community members to find physical businesses, assess their hiring potential, and organize direct outreach.
          </p>

          <h3 className="text-lg font-bold text-slate-900">Our Data Sourcing Methodology</h3>
          <p>
            We take data integrity and transparency seriously. Our platform scores local businesses using publicly available signals:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-xs">
            <li><strong>Commercial Signals:</strong> Public business ratings, review counts, active booking widgets, website age, and directory presence.</li>
            <li><strong>Hiring Tendencies:</strong> Historic public hiring activity in related categories or signals of organizational growth.</li>
            <li><strong>Outreach Suitability:</strong> Whether the business maintains simple contact paths (like a direct public phone, email, or physical location) suitable for professional introductions.</li>
          </ul>
          <p className="text-xs text-slate-500 bg-slate-100 p-3.5 rounded border border-slate-200">
            <strong>Note on data compliance:</strong> BizNearYou does not display any private contact channels, paywalled records, or private employee lists. All information presented is compiled from public directory listings. We are strictly compliant with opt-out standards.
          </p>

          <h3 className="text-lg font-bold text-slate-900">Our core values</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div className="p-4 bg-white rounded-xl border border-slate-100 shadow-sm space-y-1">
              <h4 className="font-bold text-slate-900">Proactive Careers</h4>
              <p className="text-slate-500 leading-relaxed">We believe displaying professional initiative directly to local businesses builds stronger, more authentic career starts.</p>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-100 shadow-sm space-y-1">
              <h4 className="font-bold text-slate-900">Supporting Local Commerce</h4>
              <p className="text-slate-500 leading-relaxed">We introduce talented, proactive local residents directly to physical businesses in their community, supporting regional growth.</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8 text-center space-y-4 shadow-xs">
          <h3 className="font-bold text-base sm:text-lg">Ready to find businesses in your community?</h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 max-w-xl mx-auto leading-relaxed">
            Join thousands of proactive candidates who map, score, and contact local companies directly.
          </p>
          <button
            onClick={() => onLogin(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-6 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
          >
            Create your free account
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// 15. Strategic Guide: How to Prioritize Businesses (/how-to-prioritize-businesses)
export function HowToPrioritizeBusinessesPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "How to Prioritize Local Businesses for Job Outreach",
    description: "Learn how to categorize and prioritize physical businesses. Find out which clinics, agencies, and small trades are most responsive to direct sourcing.",
    canonicalUrl: "https://getbiznearyou.com/how-to-prioritize-businesses",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "How to Prioritize Local Businesses for Job Outreach",
      "description": "Understand how to prioritize local companies based on location proximity, commercial activity signals, and administrative bottlenecks.",
      "url": "https://getbiznearyou.com/how-to-prioritize-businesses"
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 min-h-screen py-12 md:py-16 text-slate-850"
    >
      <div className="max-w-4xl mx-auto px-6 space-y-12 text-left">
        {/* Header Block */}
        <SeoBreadcrumbs items={[{ label: "Guides", path: "/guides" }, { label: "How to Prioritize Businesses", path: "/how-to-prioritize-businesses" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight">
            How to Prioritize Local Businesses Before Direct Outreach
          </h1>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
            Direct outreach is highly effective, but your time is finite. To secure the highest yield on your effort, you must evaluate and score nearby businesses before sending a single email or placing a call. Here is our strategic prioritizing guide.
          </p>
        </div>

        {/* Core Methodology */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 md:p-8 space-y-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" /> The 3-Tier Sourcing Framework
          </h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Instead of emailing every company in alphabetical order, organize your local target list into three clean priority tiers:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
            <div className="p-5 rounded-xl bg-emerald-50/50 border border-emerald-100 space-y-3">
              <span className="text-[10px] font-black text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded uppercase">Tier 1: High Signal</span>
              <h3 className="font-bold text-slate-900 text-sm">Growing & Active</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Businesses with high review velocity, modern active websites, or recent physical expansions. They are busy and frequently need support.
              </p>
            </div>
            <div className="p-5 rounded-xl bg-blue-50/50 border border-blue-100 space-y-3">
              <span className="text-[10px] font-black text-blue-800 bg-blue-100 px-2 py-0.5 rounded uppercase">Tier 2: Solid Matches</span>
              <h3 className="font-bold text-slate-900 text-sm">Stable Local Staples</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Established dental clinics, design firms, or law practices with consistent customer reviews but slightly outdated digital booking. Great targets to pitch.
              </p>
            </div>
            <div className="p-5 rounded-xl bg-slate-100/50 border border-slate-200 space-y-3">
              <span className="text-[10px] font-black text-slate-700 bg-slate-200 px-2 py-0.5 rounded uppercase">Tier 3: Low Match</span>
              <h3 className="font-bold text-slate-900 text-sm">Quiet / Closed</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                No customer reviews in over 12 months, inactive or broken web links, or strictly corporate remote entities with no local decision-making authority.
              </p>
            </div>
          </div>
        </div>

        {/* Key Signals */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-slate-900">What Hiring Signals Tell You</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            A "hiring signal" is an objective, public indicator of commercial activity. When analyzing local businesses on the BizNearYou platform, look for these specific green flags:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-5 bg-white rounded-xl border border-slate-150 space-y-2">
              <h3 className="font-bold text-slate-900 text-sm">Website Age & Frameworks</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                An upgraded website indicates investment in local marketing. Outdated sites present an opportunity to pitch front-desk optimization or junior design/web support.
              </p>
            </div>
            <div className="p-5 bg-white rounded-xl border border-slate-150 space-y-2">
              <h3 className="font-bold text-slate-900 text-sm">Google Review Velocity</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Receiving 5+ reviews per week means the physical location is busy. Busy operations are often understaffed and require immediate assistant help.
              </p>
            </div>
            <div className="p-5 bg-white rounded-xl border border-slate-150 space-y-2">
              <h3 className="font-bold text-slate-900 text-sm">Missing Digital Conveniences</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                If a clinic or shop lacks an online scheduler, they spend hours on phone booking. You can pitch your services to streamline administrative workflow.
              </p>
            </div>
            <div className="p-5 bg-white rounded-xl border border-slate-150 space-y-2">
              <h3 className="font-bold text-slate-900 text-sm">Physical Locations Density</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Multiple offices in the same county indicate structured regional growth. They are highly suitable targets for student internships or clinical placements.
              </p>
            </div>
          </div>
        </div>

        {/* Actionable CRM CTA */}
        <div className="bg-indigo-50 dark:bg-indigo-950/60 text-slate-900 dark:text-white border border-indigo-200 dark:border-indigo-900/60 rounded-2xl p-6 md:p-8 space-y-4 shadow-xs">
          <h2 className="text-xl font-bold text-indigo-950 dark:text-white">Implement This Strategy in Your Private Workspace</h2>
          <p className="text-xs text-indigo-800 dark:text-indigo-200 max-w-2xl leading-relaxed">
            Don't waste time on spreadsheets. Sign up for BizNearYou to automatically score nearby businesses, organize your target pipeline in our integrated CRM, and copy pre-written professional outreach scripts.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={() => onLogin(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Start Sourcing Now
            </button>
            <button
              onClick={() => onNavigate("/how-to-organize-local-job-search")}
              className="bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-indigo-900 dark:text-indigo-200 border border-indigo-300 dark:border-indigo-700 font-semibold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Learn to Track Outreach
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 16. Strategic Guide: How to Organize Local Job Search (/how-to-organize-local-job-search)
export function HowToOrganizeLocalJobSearchPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  // Dynamic SEO Metadata Injection
  useSeoMetadata({
    title: "How to Organize Your Local Direct Outreach Pipeline",
    description: "Learn how to build a highly organized system to manage direct outbound communication with nearby dental clinics, pet care clinics, and small trade offices.",
    canonicalUrl: "https://getbiznearyou.com/how-to-organize-local-job-search",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "How to Organize Your Local Direct Outreach Pipeline",
      "description": "A comprehensive guide on establishing structured tracking, calendar management, and professional follow-up intervals for local job seeking.",
      "url": "https://getbiznearyou.com/how-to-organize-local-job-search"
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 min-h-screen py-12 md:py-16 text-slate-850"
    >
      <div className="max-w-4xl mx-auto px-6 space-y-12 text-left">
        {/* Header Block */}
        <SeoBreadcrumbs items={[{ label: "Guides", path: "/guides" }, { label: "How to Organize Local Job Search", path: "/how-to-organize-local-job-search" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight">
            How to Organize Your Local Direct Outreach Pipeline
          </h1>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
            The difference between a failed job search and a successful proactive placement is structured tracking. Treat your local business outreach like a professional sales pipeline.
          </p>
        </div>

        {/* Step-by-Step System */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-slate-900">The 4-Step Direct Sourcing System</h2>
          
          <div className="space-y-4 text-sm text-slate-600">
            <div className="flex gap-4 p-5 bg-white rounded-xl border border-slate-100">
              <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-extrabold text-sm flex items-center justify-center shrink-0">1</span>
              <div className="space-y-1">
                <h3 className="font-bold text-slate-900 text-sm">Discover and Map Nearby Businesses</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Search by location on BizNearYou. Identify clinics, studios, and shops within a 15-mile radius. Never target businesses that are too far to commute comfortably.
                </p>
              </div>
            </div>

            <div className="flex gap-4 p-5 bg-white rounded-xl border border-slate-100">
              <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-extrabold text-sm flex items-center justify-center shrink-0">2</span>
              <div className="space-y-1">
                <h3 className="font-bold text-slate-900 text-sm">Evaluate Signals and Assign Priority Tiers</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Look at active customer reviews, tech setups, and commercial details. Prioritize the top 10 high-signal businesses that align perfectly with your skill set.
                </p>
              </div>
            </div>

            <div className="flex gap-4 p-5 bg-white rounded-xl border border-slate-100">
              <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-extrabold text-sm flex items-center justify-center shrink-0">3</span>
              <div className="space-y-1">
                <h3 className="font-bold text-slate-900 text-sm">Initiate Polite, Direct Contact</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Email or call the business owner directly. Use high-quality scripts that state your name, express respect for their local practice, and offer clear, value-first assistance.
                </p>
              </div>
            </div>

            <div className="flex gap-4 p-5 bg-white rounded-xl border border-slate-100">
              <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-extrabold text-sm flex items-center justify-center shrink-0">4</span>
              <div className="space-y-1">
                <h3 className="font-bold text-slate-900 text-sm">Log Responses and Follow Up Consistently</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Keep a close log of your outreach dates. If you receive no reply in 7 business days, send a polite, brief follow-up email to keep your profile on top of mind.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Metrics block */}
        <div className="bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-300 rounded-2xl p-6 md:p-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center border border-slate-200 dark:border-slate-800 shadow-xs">
          <div className="space-y-1">
            <p className="text-2xl font-black text-blue-600 dark:text-blue-450">40%</p>
            <p className="text-xs text-slate-700 dark:text-slate-400 font-bold">Higher Response Rate</p>
            <p className="text-[10px] text-slate-500">Over traditional automated job board resumes.</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400">10-15</p>
            <p className="text-xs text-slate-700 dark:text-slate-400 font-bold">Target Businesses</p>
            <p className="text-[10px] text-slate-500">Is the optimal target range for direct outreach pipelines.</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">0</p>
            <p className="text-xs text-slate-700 dark:text-slate-400 font-bold">Applicant Tracking Bots</p>
            <p className="text-[10px] text-slate-500">To block or filter out your personalized emails.</p>
          </div>
        </div>

        {/* Action Call */}
        <div className="border-t border-slate-200 pt-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-left">
            <h3 className="font-black text-slate-900 text-lg">Stop relying on random job postings</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Use the built-in BizNearYou local outreach CRM to organize and coordinate your direct sourcing.
            </p>
          </div>
          <button
            onClick={() => onLogin(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-6 py-3 rounded-lg shadow-sm transition whitespace-nowrap cursor-pointer flex items-center gap-1"
          >
            Create Your CRM Account <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// 17. Strategic Guide: How to Ask a Business if They're Hiring (/how-to-ask-if-hiring)
export function HowToAskIfHiringPage({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  const [activeChannel, setActiveChannel] = useState<"inperson" | "email" | "phone">("inperson");
  const [copiedScript, setCopiedScript] = useState(false);

  // Hook for Dynamic SEO Metadata
  useSeoMetadata({
    title: "How to Ask a Business if They're Hiring: Sourcing Scripts & Etiquette",
    description: "Learn how to respectfully ask local businesses if they are hiring. Discover professional scripts, phone conversation frameworks, in-person guidelines, and outreach tips.",
    canonicalUrl: "https://getbiznearyou.com/how-to-ask-if-hiring",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      "headline": "How to Ask a Business if They're Hiring: Sourcing Scripts & Etiquette",
      "description": "Learn how to respectfully ask local businesses if they are hiring with professional scripts, phone frameworks, in-person tips, and direct outreach strategies.",
      "author": {
        "@type": "Person",
        "name": "Alex, Creator of BizNearYou"
      },
      "publisher": {
        "@type": "Organization",
        "name": "BizNearYou",
        "logo": {
          "@type": "ImageObject",
          "url": "https://getbiznearyou.com/logo.png"
        }
      }
    }
  });

  const scripts = {
    inperson: {
      title: "In-Person Warm Walk-In Script",
      setting: "Tuesday/Thursday afternoon (2:00 PM - 4:00 PM), dress business-casual, print 5 clean resumes.",
      scriptText: `[You]: "Hi! My name is [Your Name], and I'm a local neighbor living just a few blocks away. I've been following your clinic/office for a while and really respect the excellent feedback you get from the neighborhood.

I'm currently seeking a local [part-time helper / administrative assistant / clinic intern] role, and I wanted to drop off my resume in person. I know you're incredibly busy, but is there a lead office manager or coordinator I could briefly introduce myself to, or should I leave my contact details with you?"

[If they take the resume]:
"Thank you so much! I've included my direct mobile number, email, and commute availability on the top page. I look forward to following up next week. Have a wonderful afternoon!"`
    },
    email: {
      title: "Direct Value-First Email Template",
      setting: "Send around 8:30 AM on Tuesday or Wednesday. Keep it strictly under 150 words.",
      scriptText: `Subject: Inquiry: Proactive ${"[Your Skill]"} assistance for ${"[Business Name]"}

Dear [Owner or Office Manager Name],

I hope you are having an active week.

My name is [Your Name], and I am a local resident living nearby in [Neighborhood]. I've been researching dynamic businesses in our community and highly admire the work you do at [Business Name], especially your excellent customer reputation.

I am an aspiring [administrative assistant / clinical helper / web coordinator] with experience in [list 1-2 skills, e.g., filing, customer check-ins, or social updates]. I am reaching out directly to check if you have any unadvertised assistance needs or upcoming administrative bottlenecks on your team. 

I've attached my clean, 1-page resume for your reference. Would you be open to a 5-minute phone chat next Tuesday afternoon to see if my background might support your office operations in the future?

Sincerely,

[Your Name]
[Your Phone Number]
[Your LinkedIn Profile]`
    },
    phone: {
      title: "Low-Pressure Telephone Introduction",
      setting: "Call during off-peak hours (e.g., 10:00 AM or 2:30 PM). Speak slowly and cheerfully.",
      scriptText: `[Receptionist]: "Thank you for calling [Business Name], how can I assist you today?"

[You]: "Hi! My name is [Your Name], and I'm a local resident. I'm actually a big admirer of your local office/practice on maps. I'm currently looking for a [part-time office assistant / helper role] in our neighborhood, and I was wondering if I could speak very briefly with the office manager or owner to ask about any unadvertised support roles?"

[If Busy / Not Available]:
"I completely understand you're busy! Would it be better if I sent a quick email introducing myself, or is there a quieter time later this week you'd recommend I call back?"

[If Connected to Manager]:
"Hi [Manager Name], my name is [Your Name]. I'm a local resident nearby. I specialize in [your specialty] and love your work in the community. I wanted to see if you ever have a need for reliable local helpers or assistant roles. I'd love to send over my 1-page resume if you are open to keeping my details on file?"`
    }
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(scripts[activeChannel].scriptText);
    setCopiedScript(true);
    setTimeout(() => setCopiedScript(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 min-h-screen py-12 md:py-16 text-slate-850"
    >
      <div className="max-w-4xl mx-auto px-6 space-y-12 text-left">
        {/* Breadcrumb / Back Button */}
        <SeoBreadcrumbs items={[{ label: "Guides", path: "/guides" }, { label: "How to Ask if They're Hiring", path: "/how-to-ask-if-hiring" }]} onNavigate={onNavigate} />
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight">
            How to Ask a Business if They're Hiring: Sourcing Scripts & Etiquette
          </h1>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
            Many local businesses (like private dental practices, boutique law firms, animal clinics, and local trades) need helper roles but never list them online because they find recruitment portals tedious or expensive. Asking directly is a proven path to unadvertised positions—provided you do so respectfully and professionally.
          </p>
        </div>

        {/* Strategic Sourcing Disclaimer */}
        <div className="bg-white rounded-xl p-6 border border-slate-200/80 shadow-sm space-y-4">
          <h2 className="font-bold text-slate-900 text-sm sm:text-base flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-600 shrink-0" /> Sourcing vs. Job Demanding
          </h2>
          <p className="text-xs text-slate-655 leading-relaxed">
            When you contact a business displaying high signals on BizNearYou, remember: <strong>a commercial signal is not a job posting</strong>. Do not show up or email demanding a job. Instead, frame your introduction as a polite inquiry. Your goal is to introduce your availability, show interest in their local footprint, and offer clear, specific helper capabilities.
          </p>
        </div>

        {/* The Three Sourcing Channels */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-slate-900">The 3 Professional Outreach Channels</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-5 bg-white rounded-xl border border-slate-150 space-y-2.5">
              <span className="w-7 h-7 rounded-full bg-blue-50 text-blue-700 font-extrabold text-xs flex items-center justify-center border border-blue-100">1</span>
              <h3 className="font-bold text-slate-950 text-xs sm:text-sm">In-Person Warm Walk-In</h3>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Best for customer-facing, retail, or local medical support. Walk in during off-peak hours, present a clean resume, and ask for the general office lead or practice manager.
              </p>
            </div>
            
            <div className="p-5 bg-white rounded-xl border border-slate-150 space-y-2.5">
              <span className="w-7 h-7 rounded-full bg-blue-50 text-blue-700 font-extrabold text-xs flex items-center justify-center border border-blue-100">2</span>
              <h3 className="font-bold text-slate-950 text-xs sm:text-sm">Value-First Email Inquiry</h3>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Best for creative studios, tech agencies, and clerical roles. Address the business owner by name, keep your text strictly under 150 words, and attach your PDF resume.
              </p>
            </div>
            
            <div className="p-5 bg-white rounded-xl border border-slate-150 space-y-2.5">
              <span className="w-7 h-7 rounded-full bg-blue-50 text-blue-700 font-extrabold text-xs flex items-center justify-center border border-blue-100">3</span>
              <h3 className="font-bold text-slate-950 text-xs sm:text-sm">Cheerfully Polite Phone Call</h3>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Best for local service trades and medical reception backups. Ask for the manager politely, expect gatekeepers, and have a clear 30-second value pitch ready.
              </p>
            </div>
          </div>
        </div>

        {/* Interactive Script Switcher */}
        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-2xl p-6 md:p-8 space-y-6 shadow-sm border border-slate-200 dark:border-slate-800 text-left">
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Actionable Outreach Scripts</h2>
            <p className="text-xs text-slate-600 dark:text-slate-400">Select a communication channel to access our copyable scripts and settings:</p>
          </div>

          <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto">
            {(["inperson", "email", "phone"] as const).map((ch) => (
              <button
                key={ch}
                onClick={() => {
                  setActiveChannel(ch);
                  setCopiedScript(false);
                }}
                className={`px-4 py-2 text-xs font-bold rounded-lg transition capitalize shrink-0 cursor-pointer ${
                  activeChannel === ch
                    ? "bg-blue-600 text-white"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/50"
                }`}
              >
                {ch === "inperson" ? "In-Person Script" : ch === "email" ? "Cold Email Script" : "Phone Call Script"}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            <div className="bg-blue-50/70 dark:bg-slate-850 p-4 rounded-xl border border-blue-150 dark:border-slate-800 space-y-1">
              <h3 className="text-xs font-bold text-blue-700 dark:text-blue-400">{scripts[activeChannel].title}</h3>
              <p className="text-[10px] text-slate-600 dark:text-slate-400 leading-normal"><span className="font-bold text-slate-800 dark:text-slate-300">Optimal Setting: </span>{scripts[activeChannel].setting}</p>
            </div>

            <div className="bg-slate-50 dark:bg-slate-950 p-5 rounded-xl font-mono text-[11px] text-slate-800 dark:text-slate-100 leading-relaxed relative border border-slate-200 dark:border-slate-800 max-h-80 overflow-y-auto">
              <button
                onClick={handleCopyScript}
                className="absolute top-3 right-3 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-300 font-sans text-[10px] font-semibold px-2.5 py-1 rounded flex items-center gap-1 cursor-pointer transition shadow-xs border border-slate-200 dark:border-slate-700/40"
              >
                {copiedScript ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-500" /> Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" /> Copy Script
                  </>
                )}
              </button>
              <pre className="whitespace-pre-wrap font-mono">{scripts[activeChannel].scriptText}</pre>
            </div>
          </div>
        </div>

        {/* Professional Etiquette Checklist */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
          <div className="bg-white p-6 rounded-xl border border-slate-150 space-y-4 text-left">
            <h3 className="font-bold text-slate-950 text-sm sm:text-base flex items-center gap-1.5 text-emerald-700">
              <CheckCircle className="w-5 h-5" /> Outreach DOs
            </h3>
            <ul className="space-y-2.5 text-xs text-slate-655 leading-relaxed pl-1.5">
              <li>• <strong>Address decision makers by name</strong>: A little lookup research on maps or directories goes an incredibly long way.</li>
              <li>• <strong>Keep it short and punchy</strong>: Busy office managers and owners usually read emails or listen to voicemails on their phones.</li>
              <li>• <strong>Highlight local availability</strong>: Small businesses love local candidates because they are reliable, prompt, and care about the neighborhood.</li>
              <li>• <strong>Follow up politely</strong>: Send a polite follow-up email after exactly 7 business days to keep your application on top of mind.</li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-150 space-y-4 text-left">
            <h3 className="font-bold text-slate-950 text-sm sm:text-base flex items-center gap-1.5 text-rose-700">
              <AlertCircle className="w-5 h-5 text-rose-600" /> Outreach DON'Ts
            </h3>
            <ul className="space-y-2.5 text-xs text-slate-655 leading-relaxed pl-1.5">
              <li>• <strong>Don't drop in during peak rushes</strong>: Never visit a restaurant during lunch/dinner, or a dentist on busy Monday mornings.</li>
              <li>• <strong>Don't copy-paste robotic spam</strong>: Sending 100 template emails with generic fonts will immediately be marked as trash.</li>
              <li>• <strong>Don't demand an immediate job</strong>: Frame your ask around helper roles, interns, and future operational support.</li>
              <li>• <strong>Don't bypass gatekeepers rudely</strong>: Be incredibly respectful to clinic receptionists; they have massive influence on the hiring manager.</li>
            </ul>
          </div>
        </div>

        {/* CRM Call to Action */}
        <div className="bg-indigo-50 dark:bg-indigo-950/60 text-slate-900 dark:text-white border border-indigo-200 dark:border-indigo-900/60 rounded-2xl p-6 md:p-8 space-y-4 shadow-xs text-center">
          <h2 className="text-xl md:text-2xl font-black text-indigo-950 dark:text-white">Organize Your Direct Outreach Today</h2>
          <p className="text-xs text-indigo-800 dark:text-indigo-200 max-w-2xl mx-auto leading-relaxed">
            BizNearYou is more than just guides. It's a private tracking CRM. Search nearby businesses, evaluate public activity signals, look up contact routes, and manage your follow-ups to stay 100% organized.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <button
              onClick={() => onNavigate("/dashboard")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Go to CRM Dashboard
            </button>
            <button
              onClick={() => onNavigate("/")}
              className="bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-indigo-900 dark:text-indigo-200 border border-indigo-300 dark:border-indigo-700 font-bold text-xs px-5 py-2.5 rounded-lg shadow-xs transition cursor-pointer"
            >
              Search Local Businesses
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 18. Dedicated Page: Why Choose BizNearYou (/why-biznearyou)
export function PublicWhyBizNearYou({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  useSeoMetadata({
    title: "Why Choose BizNearYou - Proactive Local Business Discovery",
    description: "Discover why job seekers, trade specialists, students, and career switchers choose BizNearYou over national job ad boards.",
    canonicalUrl: "https://getbiznearyou.com/why-biznearyou",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://getbiznearyou.com/why-biznearyou",
          "url": "https://getbiznearyou.com/why-biznearyou",
          "name": "Why Choose BizNearYou for Local Job Discovery",
          "description": "Discover why job seekers choose BizNearYou over traditional paid job boards."
        },
        {
          "@type": "FAQPage",
          "@id": "https://getbiznearyou.com/why-biznearyou#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Why search local businesses before job ads are posted?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Much of local small business hiring happens through direct inquiry before a formal job posting is published. Reaching out directly allows candidates to connect with management before crowded job board applications flood in."
              }
            },
            {
              "@type": "Question",
              "name": "Who is BizNearYou designed for?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "BizNearYou is designed for job seekers seeking local employment, trade workers targeting local contractors, students looking for nearby internships, and career switchers wanting short commutes."
              }
            }
          ]
        }
      ]
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 dark:bg-slate-950 min-h-screen py-12 md:py-16 text-slate-850 dark:text-slate-100"
    >
      <div className="max-w-4xl mx-auto px-6 space-y-12 text-left">
        <SeoBreadcrumbs items={[{ label: "Why BizNearYou", path: "/why-biznearyou" }]} onNavigate={onNavigate} />

        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold">
            <Sparkles className="w-3.5 h-3.5" /> Proactive Employer Discovery
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
            Why Choose BizNearYou Over Traditional Job Boards?
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-base md:text-lg leading-relaxed">
            Traditional job portals display only companies that pay expensive monthly advertising fees ($300–$1,000+). However, many local small businesses hire through word-of-mouth or direct contact rather than online ad posts. BizNearYou maps verified local commercial businesses, calculates public growth indicators, and gives you tools to organize direct outreach.
          </p>
        </div>

        {/* Comparison Overview Card */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 shadow-xs space-y-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">How BizNearYou Compares</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs md:text-sm border-collapse">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400">
                  <th className="py-3 px-4 font-bold">Feature / Capability</th>
                  <th className="py-3 px-4 font-bold text-slate-600 dark:text-slate-400">Traditional Ad Boards</th>
                  <th className="py-3 px-4 font-bold text-blue-600 dark:text-blue-400">BizNearYou Platform</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                <tr>
                  <td className="py-3.5 px-4 font-semibold text-slate-900 dark:text-slate-100">Business Coverage</td>
                  <td className="py-3.5 px-4 text-slate-500">Only paying advertisers (~30%)</td>
                  <td className="py-3.5 px-4 font-bold text-blue-700 dark:text-blue-300">100% of local verified commercial businesses</td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-semibold text-slate-900 dark:text-slate-100">Applicant Competition</td>
                  <td className="py-3.5 px-4 text-slate-500">Massive (500+ applicants per listing)</td>
                  <td className="py-3.5 px-4 font-bold text-blue-700 dark:text-blue-300">First-mover direct outreach before job ads go live</td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-semibold text-slate-900 dark:text-slate-100">Employer Focus</td>
                  <td className="py-3.5 px-4 text-slate-500">Corporate recruiters & agencies</td>
                  <td className="py-3.5 px-4 font-bold text-blue-700 dark:text-blue-300">Local small business owners & office managers</td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-semibold text-slate-900 dark:text-slate-100">Business Intelligence</td>
                  <td className="py-3.5 px-4 text-slate-500">Static text descriptions</td>
                  <td className="py-3.5 px-4 font-bold text-blue-700 dark:text-blue-300">Opportunity Scores & Public Hiring Signals</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Who Uses BizNearYou */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Who Is BizNearYou Built For?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
                <GraduationCap className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">Students & Recent Graduates</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Find local internships, summer roles, and part-time jobs within short walking or driving distance without competing against national applicant pools.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                <Briefcase className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">Trade & Service Specialists</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Discover local HVAC, electrical, dental, legal, plumbing, and automotive contractors in your exact ZIP code to pitch your skills directly.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center font-bold">
                <Compass className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">Career Switchers</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Target stable, well-established local businesses with strong review momentum and contact them directly about administrative helper or apprentice roles.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold">
                <MapPin className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">Short-Commute Seekers</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Cut down travel time by locating every commercial business within a 5, 10, or 15-minute radius from your home address.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-blue-600 text-white rounded-2xl p-8 space-y-4 text-center">
          <h2 className="text-2xl font-black">Ready to Discover Local Employers?</h2>
          <p className="text-sm text-blue-100 max-w-xl mx-auto leading-relaxed">
            Search nearby businesses, inspect opportunity scores, and start building your custom outreach list today.
          </p>
          <div className="pt-2 flex justify-center gap-3">
            <button
              onClick={() => onNavigate("/")}
              className="bg-white text-blue-700 hover:bg-blue-50 font-bold text-xs px-6 py-3 rounded-lg shadow-sm transition cursor-pointer"
            >
              Start Searching Now
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 19. Dedicated Page: Frequently Asked Questions (/faq)
export function PublicFaq({ onNavigate, onLogin }: Omit<PublicPageProps, "currentPath">) {
  const [searchTerm, setSearchTerm] = useState("");
  const [openCategory, setOpenCategory] = useState<string>("all");

  useSeoMetadata({
    title: "Frequently Asked Questions (FAQ) | BizNearYou",
    description: "Get clear, factual answers about BizNearYou local business discovery, Opportunity Scores, Hiring Signals, data collection, and direct outreach.",
    canonicalUrl: "https://getbiznearyou.com/faq",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          "@id": "https://getbiznearyou.com/faq#faq",
          "url": "https://getbiznearyou.com/faq",
          "name": "BizNearYou Frequently Asked Questions",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is BizNearYou?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "BizNearYou is a local employer discovery engine and proactive job search CRM. It maps real physical businesses in your commute radius and gives job seekers direct access to unposted job opportunities."
              }
            },
            {
              "@type": "Question",
              "name": "How are Opportunity Scores calculated?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Opportunity Scores (1-100) are generated by evaluating phone validity, domain email availability, review count stability, website operational status, and business age."
              }
            },
            {
              "@type": "Question",
              "name": "Where does BizNearYou get its business data?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "BizNearYou aggregates public commercial information from official Google Maps Platform APIs, official state Secretary of State business registries, public web domain records, and public review platforms."
              }
            },
            {
              "@type": "Question",
              "name": "Does BizNearYou sell user data or contact lists?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. BizNearYou never sells user personal data, resume information, or search history to third parties."
              }
            }
          ]
        }
      ]
    }
  });

  const faqs = [
    {
      category: "Basics",
      q: "What is BizNearYou?",
      a: "BizNearYou is a local employer discovery engine and proactive job search CRM. Instead of displaying only paid job ads, BizNearYou maps 100% of verified local commercial businesses in your commute radius, evaluates public growth indicators, and helps you pitch decision makers directly."
    },
    {
      category: "Basics",
      q: "How does BizNearYou differ from traditional job boards?",
      a: "Traditional job boards list only companies that pay expensive monthly advertising fees ($300-$1,000+). Many local small businesses hire through direct inquiries rather than posting online ads. BizNearYou indexes physical businesses across 100+ categories regardless of ad budget, unlocking unposted local job opportunities."
    },
    {
      category: "Scores & Signals",
      q: "What is an Opportunity Score?",
      a: "An Opportunity Score is a 1-100 composite rating evaluating an employer's contactability (valid phone and domain email), website operational status, business stability, and customer review momentum to help job seekers prioritize high-yield outreach targets."
    },
    {
      category: "Scores & Signals",
      q: "What are Hiring Signals?",
      a: "Hiring Signals are public commercial metrics—such as recent customer review spikes, new branch locations, website service expansions, and operational updates—that indicate a business is growing and likely expanding its team."
    },
    {
      category: "Scores & Signals",
      q: "Does a high Hiring Signal guarantee a job opening?",
      a: "No. A Hiring Signal indicates commercial growth and activity, not an explicit job ad. It signals that the business is active and worth contacting proactively."
    },
    {
      category: "Data & Privacy",
      q: "Where does BizNearYou obtain its business data?",
      a: "Data is aggregated from public commercial records including Google Maps Platform Places APIs, Secretary of State corporate registration portals, public domain WHOIS records, and verified business web pages."
    },
    {
      category: "Data & Privacy",
      q: "Does BizNearYou sell user search history or personal contact data?",
      a: "No. BizNearYou maintains a strict privacy policy. We never sell user personal information, resume documents, or search queries to third-party data brokers."
    },
    {
      category: "Data & Privacy",
      q: "How can a business owner update or remove their listing?",
      a: "Business owners can submit a verification or removal request via our Contact page, and verified updates or removals are processed within 24-48 hours."
    },
    {
      category: "Outreach & CRM",
      q: "How should I contact a business discovered on BizNearYou?",
      a: "We recommend a value-first approach: address the owner or manager by name, keep your email or call brief (<150 words / 30 seconds), highlight your local availability, and offer specific administrative or helper capabilities."
    },
    {
      category: "Outreach & CRM",
      q: "Is the BizNearYou CRM free to use?",
      a: "Yes! BizNearYou offers a generous Free tier that includes local employer searches, opportunity scores, and pipeline tracking. Pro plans offer expanded search radius and batch export capabilities."
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = openCategory === "all" || faq.category.toLowerCase() === openCategory.toLowerCase();
    const matchesSearch = faq.q.toLowerCase().includes(searchTerm.toLowerCase()) || faq.a.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 dark:bg-slate-950 min-h-screen py-12 md:py-16 text-slate-850 dark:text-slate-100"
    >
      <div className="max-w-4xl mx-auto px-6 space-y-10 text-left">
        <SeoBreadcrumbs items={[{ label: "Frequently Asked Questions", path: "/faq" }]} onNavigate={onNavigate} />

        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold">
            <HelpCircle className="w-3.5 h-3.5" /> Direct Answers Engine
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            Find direct, transparent answers regarding local business discovery, Opportunity Scores, public data sources, and direct employer outreach.
          </p>
        </div>

        {/* Filter & Search Bar */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs">
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto w-full sm:w-auto">
            {["all", "Basics", "Scores & Signals", "Data & Privacy", "Outreach & CRM"].map(cat => (
              <button
                key={cat}
                onClick={() => setOpenCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition shrink-0 cursor-pointer ${
                  openCategory === cat
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                {cat === "all" ? "All Questions" : cat}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, idx) => (
              <div key={idx} className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-800 shadow-xs space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                    {faq.category}
                  </span>
                </div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white">
                  {faq.q}
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed pt-1">
                  {faq.a}
                </p>
              </div>
            ))
          ) : (
            <div className="bg-white dark:bg-slate-900 p-8 rounded-xl text-center text-slate-500 border border-slate-200 dark:border-slate-800">
              No questions found matching "{searchTerm}". Try another search term.
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// 20. Dedicated Page: How Data Is Collected (/how-data-is-collected)
export function PublicHowDataIsCollected({ onNavigate }: Omit<PublicPageProps, "currentPath">) {
  useSeoMetadata({
    title: "How Business Data Is Collected & Verified | BizNearYou",
    description: "Transparent breakdown of public APIs, state business filings, domain WHOIS records, and verification safeguards powering BizNearYou.",
    canonicalUrl: "https://getbiznearyou.com/how-data-is-collected",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TechArticle",
          "@id": "https://getbiznearyou.com/how-data-is-collected#article",
          "name": "How Business Information Is Collected and Verified",
          "description": "Detailed explanation of public data sources and validation algorithms used by BizNearYou."
        }
      ]
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 dark:bg-slate-950 min-h-screen py-12 md:py-16 text-slate-850 dark:text-slate-100"
    >
      <div className="max-w-4xl mx-auto px-6 space-y-10 text-left">
        <SeoBreadcrumbs items={[{ label: "How Data Is Collected", path: "/how-data-is-collected" }]} onNavigate={onNavigate} />

        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
            <ShieldCheck className="w-3.5 h-3.5" /> Public Data Transparency
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            How Business Information Is Collected & Verified
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            BizNearYou is committed to complete transparency regarding data provenance, verification standards, and business owner privacy. Here is an exact breakdown of how public commercial records are processed.
          </p>
        </div>

        {/* Data Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
            <h2 className="font-bold text-slate-900 dark:text-white text-base flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-600" /> 1. Google Maps Platform Places API
            </h2>
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              We query official Google Maps Platform APIs to verify real-world physical business locations, operating status, category classifications, and public customer review counts.
            </p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
            <h2 className="font-bold text-slate-900 dark:text-white text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-600" /> 2. Secretary of State Business Registrations
            </h2>
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Commercial entity registration dates and active corporate standing are cross-referenced with public state Secretary of State business search portals.
            </p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
            <h2 className="font-bold text-slate-900 dark:text-white text-base flex items-center gap-2">
              <Globe className="w-4 h-4 text-purple-600" /> 3. Domain WHOIS & Web Headers
            </h2>
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Domain registration ages, SSL security certificates, and active web header responses are inspected to confirm digital contactability and domain legitimacy.
            </p>
          </div>
          <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
            <h2 className="font-bold text-slate-900 dark:text-white text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-600" /> 4. Public Review Signals
            </h2>
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Aggregated monthly customer review velocity changes are monitored to evaluate real-time business growth and operational expansion.
            </p>
          </div>
        </div>

        {/* Safeguards */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Verification Safeguards</h2>
          <ul className="space-y-2.5 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span><strong>Closed Location Filter</strong>: Permanently closed or inactive commercial records are automatically purged from search results.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span><strong>Phone Number Validation</strong>: Telephone formats are checked against North American Numbering Plan standards.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span><strong>Owner Removal Channel</strong>: Business owners can request listing suppression or contact detail updates via our Contact form at any time.</span>
            </li>
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

// 21. Dedicated Page: How Scores Are Calculated (/how-scores-are-calculated)
export function PublicHowScoresAreCalculated({ onNavigate }: Omit<PublicPageProps, "currentPath">) {
  useSeoMetadata({
    title: "How Opportunity Scores Are Calculated | BizNearYou",
    description: "Learn the mathematical 1-100 rating algorithm evaluating local employer contactability, business longevity, website health, and review stability.",
    canonicalUrl: "https://getbiznearyou.com/how-scores-are-calculated",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TechArticle",
          "@id": "https://getbiznearyou.com/how-scores-are-calculated#article",
          "name": "How Opportunity Scores Are Calculated",
          "description": "Comprehensive explanation of the 1-100 Opportunity Score algorithm."
        }
      ]
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 dark:bg-slate-950 min-h-screen py-12 md:py-16 text-slate-850 dark:text-slate-100"
    >
      <div className="max-w-4xl mx-auto px-6 space-y-10 text-left">
        <SeoBreadcrumbs items={[{ label: "Score Calculation", path: "/how-scores-are-calculated" }]} onNavigate={onNavigate} />

        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-bold">
            <Award className="w-3.5 h-3.5" /> Rating Methodology
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            Understanding Opportunity Scores (1–100)
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            The Opportunity Score is an objective, mathematical 1–100 composite rating assigned to local employers to help job seekers prioritize targets with the highest outreach yield.
          </p>
        </div>

        {/* Weights Breakdown */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Score Components & Factor Weights</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl space-y-1 border border-slate-200 dark:border-slate-700">
              <div className="text-2xl font-black text-blue-600 dark:text-blue-400">35%</div>
              <h3 className="font-bold text-sm text-slate-900 dark:text-white">Contactability & Routing</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Verified phone numbers, custom domain email availability, and physical street address validity.</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl space-y-1 border border-slate-200 dark:border-slate-700">
              <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400">25%</div>
              <h3 className="font-bold text-sm text-slate-900 dark:text-white">Business Longevity</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">State registration age and operational history demonstrating commercial stability.</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl space-y-1 border border-slate-200 dark:border-slate-700">
              <div className="text-2xl font-black text-purple-600 dark:text-purple-400">20%</div>
              <h3 className="font-bold text-sm text-slate-900 dark:text-white">Digital Presence</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Active custom website, SSL encryption, mobile responsiveness, and online service updates.</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl space-y-1 border border-slate-200 dark:border-slate-700">
              <div className="text-2xl font-black text-amber-600 dark:text-amber-400">20%</div>
              <h3 className="font-bold text-sm text-slate-900 dark:text-white">Review Stability</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Customer feedback volume and positive rating consistency reflecting active clientele.</p>
            </div>
          </div>
        </div>

        {/* Score Tiers */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Score Tier Interpretation</h2>
          <div className="space-y-3">
            <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl flex items-center justify-between">
              <div>
                <span className="font-extrabold text-emerald-800 dark:text-emerald-300 text-sm">80 – 100: High Yield Target</span>
                <p className="text-xs text-emerald-700 dark:text-emerald-400">Excellent contactability, active website, established local footprint.</p>
              </div>
              <span className="px-3 py-1 bg-emerald-600 text-white font-black text-xs rounded-full">High</span>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-xl flex items-center justify-between">
              <div>
                <span className="font-extrabold text-blue-800 dark:text-blue-300 text-sm">60 – 79: Solid Commercial Lead</span>
                <p className="text-xs text-blue-700 dark:text-blue-400">Valid phone and address, moderate digital presence, steady operations.</p>
              </div>
              <span className="px-3 py-1 bg-blue-600 text-white font-black text-xs rounded-full">Solid</span>
            </div>
            <div className="p-4 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center justify-between">
              <div>
                <span className="font-extrabold text-slate-800 dark:text-slate-200 text-sm">&lt; 60: Lower Contactability</span>
                <p className="text-xs text-slate-600 dark:text-slate-400">Limited public web channels or unverified contact details; requires in-person visit.</p>
              </div>
              <span className="px-3 py-1 bg-slate-500 text-white font-black text-xs rounded-full">Basic</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 22. Dedicated Page: How Hiring Signals Work (/how-hiring-signals-work)
export function PublicHowHiringSignalsWork({ onNavigate }: Omit<PublicPageProps, "currentPath">) {
  useSeoMetadata({
    title: "How Hiring Signals Work | BizNearYou Methodology",
    description: "Understand how review momentum, facility expansion, and digital service updates reveal expanding local employers.",
    canonicalUrl: "https://getbiznearyou.com/how-hiring-signals-work",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TechArticle",
          "@id": "https://getbiznearyou.com/how-hiring-signals-work#article",
          "name": "How Hiring Signals Work",
          "description": "In-depth guide to commercial growth metrics and hiring likelihood indicators."
        }
      ]
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 dark:bg-slate-950 min-h-screen py-12 md:py-16 text-slate-850 dark:text-slate-100"
    >
      <div className="max-w-4xl mx-auto px-6 space-y-10 text-left">
        <SeoBreadcrumbs items={[{ label: "Hiring Signals Method", path: "/how-hiring-signals-work" }]} onNavigate={onNavigate} />

        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold">
            <TrendingUp className="w-3.5 h-3.5" /> Growth Intelligence
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            How Hiring Signals Work
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            Hiring Signals are public commercial activity indicators that suggest a local business is expanding its operations, increasing client volume, and likely in need of helper support.
          </p>
        </div>

        {/* The 4 Core Signals */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">The 4 Primary Growth Signals</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Signal 1</span>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">Customer Review Velocity Spikes</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                A sudden spike in monthly customer reviews indicates increased foot traffic, client demand, and heightened operational burden on existing staff.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Signal 2</span>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">Facility & Branch Expansion</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                New commercial address filings or physical suite expansions indicate physical space growth requiring additional reception or logistics helpers.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
              <span className="text-xs font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider">Signal 3</span>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">Website Service Page Additions</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Adding new service categories or product offerings on their website indicates commercial diversification requiring specialized assistants.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
              <span className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Signal 4</span>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">Operating Hour Changes</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Extending weekend or evening operating hours signals increased labor demands that are often filled with part-time or seasonal help.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// 23. Dedicated Page: Why Businesses Before Job Listings (/why-businesses-before-job-listings)
export function PublicWhyBusinessesBeforeJobListings({ onNavigate }: Omit<PublicPageProps, "currentPath">) {
  useSeoMetadata({
    title: "Why Businesses Appear Before Job Listings | BizNearYou",
    description: "Why local small business jobs are often filled through direct contact rather than public job boards, and how mapping businesses unlocks unadvertised roles.",
    canonicalUrl: "https://getbiznearyou.com/why-businesses-before-job-listings",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://getbiznearyou.com/why-businesses-before-job-listings#article",
          "name": "Why Businesses Appear Before Job Listings",
          "description": "Explaining the hidden job market and direct outreach advantage."
        }
      ]
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 dark:bg-slate-950 min-h-screen py-12 md:py-16 text-slate-850 dark:text-slate-100"
    >
      <div className="max-w-4xl mx-auto px-6 space-y-10 text-left">
        <SeoBreadcrumbs items={[{ label: "Unposted Opportunities", path: "/why-businesses-before-job-listings" }]} onNavigate={onNavigate} />

        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-bold">
            <Compass className="w-3.5 h-3.5" /> Market Economics
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            Why BizNearYou Maps Businesses Before Jobs Are Posted
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            Understanding small business economics explains why waiting for a formal job posting puts job seekers at a massive disadvantage.
          </p>
        </div>

        {/* Small Business Economics */}
        <div className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">The Small Business Hiring Dilemma</h2>
          <div className="space-y-4 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            <p>
              When a small business owner (like a dentist, plumbing contractor, accounting office, or boutique law firm) needs helper support, they face three frustrating hurdles with online job boards:
            </p>
            <ol className="list-decimal pl-5 space-y-2">
              <li><strong>High Listing Costs</strong>: Major job sites charge $300 to $1,000+ per month per post.</li>
              <li><strong>Applicant Noise</strong>: A public posting receives hundreds of automated resumes from unqualified out-of-state applicants.</li>
              <li><strong>Time Constraints</strong>: Small business owners do not have HR departments to filter through 400 resumes.</li>
            </ol>
            <p className="bg-amber-50 dark:bg-amber-950/40 p-4 rounded-xl border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200 font-medium">
              Result: Most small business owners hire through word-of-mouth or hold off until a polite, qualified local candidate reaches out directly.
            </p>
          </div>
        </div>

        {/* First Mover Advantage */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">The First-Mover Outreach Advantage</h2>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            By mapping verified commercial businesses and evaluating growth signals, BizNearYou allows job seekers to introduce themselves <strong>before</strong> an owner pays for an online job posting. When you send a respectful, concise email or drop off a clean resume, you solve the owner's hiring problem without forcing them to spend money or wade through applicant spam.
          </p>
        </div>
      </div>
    </motion.div>
  );
}



// 18. Public 404 Page (Page Not Found)
export function PageNotFound({ onNavigate }: { onNavigate: (path: string) => void }) {
  useSeoMetadata({
    title: "Page Not Found | BizNearYou",
    description: "The page you are looking for does not exist. Return to BizNearYou to discover local jobs and organize outreach.",
    canonicalUrl: "https://getbiznearyou.com/404",
    robots: "noindex, nofollow"
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-slate-50 text-slate-800 py-20 px-6 min-h-[70vh] flex items-center justify-center font-sans"
    >
      <div className="max-w-xl mx-auto text-center space-y-8 animate-fade-in">
        <div className="space-y-4">
          <div className="inline-flex items-center justify-center bg-slate-100 text-slate-600 rounded-full w-20 h-20 mx-auto shadow-sm border border-slate-200">
            <span className="text-2xl font-black tracking-tight font-mono text-slate-800">404</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight mt-6">
            Page Not Found
          </h1>
          <p className="text-base text-slate-600 max-w-md mx-auto leading-relaxed">
            We couldn’t find the page you were looking for. It might have been moved, deleted, or the URL might contain a typo.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm text-left max-w-md mx-auto space-y-4">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 font-mono">
            Suggested Pages to Visit
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <button
              onClick={() => onNavigate("/")}
              className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition text-left font-semibold text-slate-700 cursor-pointer"
            >
              <Briefcase className="w-4 h-4 text-indigo-500 shrink-0" />
              <span>Search Businesses</span>
            </button>
            <button
              onClick={() => onNavigate("/find-jobs")}
              className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition text-left font-semibold text-slate-700 cursor-pointer"
            >
              <Compass className="w-4 h-4 text-blue-500 shrink-0" />
              <span>Find Employers</span>
            </button>
            <button
              onClick={() => onNavigate("/hidden-jobs")}
              className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition text-left font-semibold text-slate-700 cursor-pointer"
            >
              <TrendingUp className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>Hidden Jobs Guide</span>
            </button>
            <button
              onClick={() => onNavigate("/local-internships")}
              className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition text-left font-semibold text-slate-700 cursor-pointer"
            >
              <GraduationCap className="w-4 h-4 text-indigo-500 shrink-0" />
              <span>Local Internships</span>
            </button>
          </div>
        </div>

        <div className="pt-4 flex flex-col sm:flex-row justify-center gap-4">
          <button
            onClick={() => onNavigate("/")}
            className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold py-3 px-6 rounded-lg shadow-sm transition cursor-pointer flex items-center justify-center gap-2"
          >
            Return to Homepage <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => onNavigate("/contact")}
            className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-bold py-3 px-6 rounded-lg transition cursor-pointer flex items-center justify-center gap-2"
          >
            Contact Support
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// 24. Dedicated Page: Pricing (/pricing)
export function PublicPricingPage({ onNavigate, onLogin, settingsAppTheme = "light" }: Omit<PublicPageProps, "currentPath">) {
  useSeoMetadata({
    title: "Pricing & Plans | BizNearYou Local Opportunity Finder",
    description: "Compare BizNearYou Free and Pro plans side-by-side. Discover local job opportunities, unlock advanced filters, and organize your proactive job search.",
    canonicalUrl: "https://getbiznearyou.com/pricing",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "BizNearYou Pricing & Plans",
      "url": "https://getbiznearyou.com/pricing",
      "description": "Compare BizNearYou Free and Pro plans side-by-side."
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="bg-slate-50 dark:bg-slate-950 min-h-screen py-10 md:py-16 text-slate-850 dark:text-slate-100"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 space-y-8 text-left">
        <SeoBreadcrumbs items={[{ label: "Pricing & Plans", path: "/pricing" }]} onNavigate={onNavigate} />

        <PricingComparisonTable
          settingsAppTheme={settingsAppTheme}
          onUpgradeClick={() => {
            if (onLogin) {
              onLogin(true);
            } else {
              onNavigate("/signup");
            }
          }}
        />
      </div>
    </motion.div>
  );
}


